/**
 * API Client Service
 *
 * Centralized HTTP client for making API requests to the Notamicro backend.
 *
 * Auth model (EPIC-001 D1, D10):
 *   - The backend posts the JWT in an HttpOnly cookie (nm_session) at login.
 *   - We pass `credentials: 'include'` so the browser sends/receives the
 *     cookie automatically — no Authorization header is read or written.
 *   - On 401 we redirect to /connexion.html (unless we are already on it), letting
 *     the user re-authenticate. No localStorage tokens are kept.
 */

import { getSessionId } from "../utils/session.js";

/**
 * Base URL de l'API backend.
 *
 * Lue au runtime depuis `window.APP_CONFIG.apiBaseUrl` (injecté par
 * /config.js, cf. partials/head.html — EPIC-069 / 69.2) ; la même build sert
 * dev/préprod/prod sans recompilation. Repli sur `http://localhost` si la
 * config n'est pas chargée (test unitaire isolé). Le préfixe `/api` a été
 * retiré des routes en 69.4 : la base pointe directement la racine de l'API.
 *
 * Exportée pour les appelants qui doivent émettre un `fetch` brut hors du
 * client `api` — typiquement le tracker de conversion (EPIC-038.4) : POST
 * `keepalive` à échec silencieux, sans la gestion d'erreur / redirection
 * 401 du client standard.
 */
export const API_BASE_URL = globalThis.APP_CONFIG?.apiBaseUrl ?? "http://localhost";

/**
 * API Configuration
 */
const API_CONFIG = {
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    "Content-Type": "application/json",
    "Accept": "application/json",
  },
};

/**
 * Custom API Error
 */
class APIError extends Error {
  constructor(message, status, data = null) {
    super(message);
    this.name = "APIError";
    this.status = status;
    this.data = data;
  }
}

/**
 * Build request headers (no auth token — the nm_session cookie is sent
 * by the browser via credentials: 'include').
 *
 * EPIC-009 / 9.7 — `X-Session-Id` est injecté systématiquement (overhead
 * nul) pour alimenter le funnel `conversion_event` côté backend. Les
 * endpoints qui émettent un event server-side (`POST /clients` et
 * sa famille pour 10.8 / 12.9 / 13.10) le rejettent par 400 si le header
 * est manquant — defense in depth, le frontend en a toujours un via
 * `getSessionId()`.
 */
function buildHeaders(customHeaders = {}) {
  return {
    ...API_CONFIG.headers,
    "X-Session-Id": getSessionId(),
    ...customHeaders,
  };
}

/**
 * Extract a user-facing error message from a backend response that
 * follows docs/conventions_api.md §4 ({error: {code, message, details?}}).
 * Falls back gracefully when the backend returns a non-conforming body
 * (e.g. an upstream proxy 502).
 */
function extractErrorMessage(data, response) {
  if (data && typeof data === "object") {
    if (data.error && typeof data.error === "object" && typeof data.error.message === "string") {
      return data.error.message;
    }
    if (typeof data.message === "string") {
      return data.message;
    }
  }
  return `HTTP ${response.status}: ${response.statusText}`;
}

/**
 * Redirect to /connexion.html on 401 — but only when we are NOT already there,
 * so an invalid login response surfaces the inline error instead of
 * triggering a reload loop.
 */
function redirectToLoginIfNeeded() {
  const path = window.location.pathname;
  if (path !== "/connexion.html") {
    window.location.assign("/connexion.html");
  }
}

/**
 * Extrait le `filename` d'un header `Content-Disposition` (RFC 6266).
 * Préfère `filename*=UTF-8''…` quand présent, sinon retombe sur `filename="…"`.
 * Retourne `null` si rien n'est exploitable.
 */
function parseFilenameFromContentDisposition(header) {
  if (!header || typeof header !== "string") return null;
  const starMatch = header.match(/filename\*\s*=\s*[^']*'[^']*'([^;]+)/i);
  if (starMatch && starMatch[1]) {
    try { return decodeURIComponent(starMatch[1].trim()); } catch (_e) { /* noop */ }
  }
  const quotedMatch = header.match(/filename\s*=\s*"([^"]+)"/i);
  if (quotedMatch && quotedMatch[1]) return quotedMatch[1];
  const bareMatch = header.match(/filename\s*=\s*([^;]+)/i);
  if (bareMatch && bareMatch[1]) return bareMatch[1].trim();
  return null;
}

function defaultExportFilename() {
  const now = new Date();
  const yyyy = now.getFullYear();
  const mm = String(now.getMonth() + 1).padStart(2, "0");
  const dd = String(now.getDate()).padStart(2, "0");
  return `notamicro-export-${yyyy}-${mm}-${dd}.zip`;
}

/**
 * Handle API response — either return the parsed body or throw an
 * APIError carrying the conventions §4 code + message.
 */
async function handleResponse(response) {
  // 204 No Content has no body.
  if (response.status === 204) {
    return null;
  }

  const contentType = response.headers.get("content-type") || "";
  let data;
  if (contentType.includes("application/json")) {
    data = await response.json();
  } else {
    data = await response.text();
  }

  if (!response.ok) {
    const message = extractErrorMessage(data, response);

    if (response.status === 401) {
      redirectToLoginIfNeeded();
      throw new APIError(message, 401, data);
    }

    throw new APIError(message, response.status, data);
  }

  return data;
}

/**
 * Make HTTP request
 */
async function request(endpoint, options = {}) {
  const url = `${API_CONFIG.baseURL}${endpoint}`;

  // FormData requests must let the browser pick the multipart boundary;
  // we drop the JSON Content-Type default for them. Le `X-Session-Id`
  // reste injecté (EPIC-009 / 9.7 — funnel §2.K).
  const isFormData = options.body instanceof FormData;
  const headers = isFormData
    ? {
        "Accept": "application/json",
        "X-Session-Id": getSessionId(),
        ...(options.headers || {}),
      }
    : buildHeaders(options.headers);

  const config = {
    ...options,
    headers,
    credentials: "include",
  };

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), API_CONFIG.timeout);

  try {
    config.signal = controller.signal;
    const response = await fetch(url, config);
    clearTimeout(timeout);
    return await handleResponse(response);
  } catch (error) {
    clearTimeout(timeout);

    if (error.name === "AbortError") {
      throw new APIError("Délai d'attente dépassé", 408);
    }

    if (error instanceof APIError) {
      throw error;
    }

    throw new APIError(error.message || "Erreur réseau", 0);
  }
}

/**
 * Cache mémoïsé de `api.getMeCached()` (EPIC-009 / 9.7). Promise partagée
 * pour toute la durée de vie de la page. Resetée à `null` sur rejet pour
 * permettre un retry au prochain appel.
 *
 * @type {Promise<object>|null}
 */
let _meCachedPromise = null;

/**
 * API Client Methods
 */
export const api = {
  async get(endpoint, params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const url = queryString ? `${endpoint}?${queryString}` : endpoint;
    return request(url, { method: "GET" });
  },

  async post(endpoint, data = {}, headers = {}) {
    return request(endpoint, {
      method: "POST",
      body: JSON.stringify(data),
      headers,
    });
  },

  async put(endpoint, data = {}) {
    return request(endpoint, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  },

  async delete(endpoint) {
    return request(endpoint, { method: "DELETE" });
  },

  async patch(endpoint, data = {}) {
    return request(endpoint, {
      method: "PATCH",
      body: JSON.stringify(data),
    });
  },

  async upload(endpoint, file, additionalData = {}, fieldName = "file", extraHeaders = {}) {
    const formData = new FormData();
    formData.append(fieldName, file);

    Object.keys(additionalData).forEach((key) => {
      formData.append(key, additionalData[key]);
    });

    // Let the browser set the multipart boundary by passing an empty
    // headers object (we still keep credentials: 'include' via request()).
    // `extraHeaders` lets callers attach e.g. `Idempotency-Key` without
    // breaking the multipart Content-Type negotiation.
    return request(endpoint, {
      method: "POST",
      body: formData,
      headers: extraHeaders,
    });
  },

  // ---------------------------------------------------------------------------
  // Auth named wrappers (EPIC-002 / 2.4) — stable call sites for the auth
  // controllers. Each one is a thin alias on top of `post()` to keep the
  // controllers free of stringly-typed endpoints.
  // ---------------------------------------------------------------------------

  async signup(payload) {
    return this.post("/auth/signup", payload);
  },

  async verifyEmail(token) {
    return this.post("/auth/verify-email", { token });
  },

  async resendVerification(email) {
    return this.post("/auth/resend-verification", { email });
  },

  // POST /auth/logout — invalide le cookie nm_session côté serveur.
  async logout() {
    return this.post("/auth/logout", {});
  },

  // GET /auth/me — returns the current session context
  // ({ user, organization }, see AuthController::serializeContext).
  // On 401, the global handler in request() redirects to /connexion.html.
  async getMe() {
    return this.get("/auth/me");
  },

  /**
   * Variante mémoïsée de `getMe()` : partage une seule promise pour toute
   * la durée de vie de la page (EPIC-009 / 9.7). Plusieurs controllers
   * Stimulus consomment `onboardingStage` au montage (`app-header`,
   * `clients-list`, futurs 10.8 / 12.9 / 13.10) — sans cache on émettrait
   * autant de GET `/auth/me`. La promise rejetée n'est **pas** mémoïsée
   * (retry possible au prochain appel).
   *
   * @returns {Promise<object>} même payload que `getMe()`.
   */
  getMeCached() {
    if (!_meCachedPromise) {
      _meCachedPromise = this.getMe().catch((err) => {
        _meCachedPromise = null;
        throw err;
      });
    }
    return _meCachedPromise;
  },

  // ---------------------------------------------------------------------------
  // Onboarding prospect (EPIC-068 / 68.1 + 68.2, §15.A).
  //   - GET   /me/onboarding-status → état des champs manquants + isComplete.
  //   - PATCH /me/onboarding        → persistance incrémentale (has-ME,
  //                                       prénom/nom). Mêmes champs en réponse.
  // Gate prospect-only côté serveur (403 si organization.status != prospect).
  // ---------------------------------------------------------------------------

  /**
   * État courant de l'onboarding prospect (champs manquants + complétude).
   *
   * @returns {Promise<{data: {
   *   prospectHasExistingMicro: boolean|null,
   *   prospectSiret: string|null,
   *   firstNameMissing: boolean,
   *   lastNameMissing: boolean,
   *   isComplete: boolean,
   * }}>}
   */
  async getOnboardingStatus() {
    return this.get("/me/onboarding-status");
  },

  /**
   * Persiste de façon incrémentale l'état d'onboarding prospect. Tous les
   * champs sont optionnels (appel étape par étape).
   *
   * @param {object} payload
   * @param {boolean|null} [payload.prospectHasExistingMicro] true / false / null (skip)
   * @param {string|null} [payload.prospectSiret] SIRET déclaré (14 chiffres) ou null
   * @param {string} [payload.firstName]
   * @param {string} [payload.usageLastName]
   * @returns {Promise<{data: {
   *   prospectHasExistingMicro: boolean|null,
   *   prospectSiret: string|null,
   *   firstNameMissing: boolean,
   *   lastNameMissing: boolean,
   *   isComplete: boolean,
   * }}>}
   */
  async updateOnboarding(payload) {
    return this.patch("/me/onboarding", payload);
  },

  // ---------------------------------------------------------------------------
  // Billing named wrappers (EPIC-003 / 3.5) — abonnement Qonto Pay.
  // Aligné sur les endpoints livrés en 3.3 (BillingController) :
  //   - POST /billing/checkout-intent → 201 { intentId, redirectUrl, … }
  //   - GET  /billing/subscriptions/me → 200 { subscription|null, recentEvents[] }
  // ---------------------------------------------------------------------------

  /**
   * Crée une intention de paiement Qonto Pay pour l'org courante.
   * Le backend déduit lui-même le parcours (A si siret null, B sinon).
   *
   * @param {object} payload
   * @param {string} [payload.cadence] - "monthly" ou "annual" (parcours B uniquement)
   * @param {boolean} [payload.addRsacFee] - parcours A : ajoute 23,21 € RSAC
   * @returns {Promise<{intentId: string, redirectUrl: string, amountHt: string, cadence: string, parcours: string}>}
   */
  async createCheckoutIntent(payload = {}) {
    return this.post("/billing/checkout-intent", payload);
  },

  /**
   * Récupère la subscription `saas active` de l'org + 10 derniers events.
   * Retourne `{subscription: null, recentEvents: []}` si org `prospect`.
   *
   * @returns {Promise<{subscription: object|null, recentEvents: Array<object>}>}
   */
  async getSubscriptionsMe() {
    return this.get("/billing/subscriptions/me");
  },

  // ---------------------------------------------------------------------------
  // EPIC-004 / 4.8 — Hydratation enrichie de /abonnement.html.
  // Endpoints livrés en 4.2 / 4.3 / 4.4 :
  //   - GET  /billing/mandates/me
  //       → { active: {…}|null, history: [{…}] }
  //   - POST /billing/manual-payment-link
  //       → 201 { intentId, redirectUrl, amountHt } (org `past_due` requis)
  //   - POST /billing/upgrade-to-annual
  //       → 201 { intentId, redirectUrl, amountHt, delta, gratuit:false }
  //         (delta > 0)
  //       → 200 { gratuit:true, cadence:'annual', renewalDate, delta:'0.00' }
  //         (cas limite §2.D : la somme déjà payée couvre l'annuel)
  // ---------------------------------------------------------------------------

  /**
   * Récupère le mandat SEPA actif de l'org + l'historique court.
   *
   * @returns {Promise<{active: object|null, history: Array<object>}>}
   */
  async getMyMandates() {
    return this.get("/billing/mandates/me");
  },

  /**
   * Génère un lien Qonto Pay one-shot pour une org en `past_due`.
   *
   * @returns {Promise<{intentId: string, redirectUrl: string, amountHt: string}>}
   */
  async createManualPaymentLink() {
    return this.post("/billing/manual-payment-link", {});
  },

  /**
   * Bascule mensuel → annuel self-service.
   *
   * @returns {Promise<{
   *   intentId?: string,
   *   redirectUrl?: string,
   *   amountHt?: string,
   *   delta?: string,
   *   gratuit: boolean,
   *   cadence?: string,
   *   renewalDate?: string,
   * }>}
   */
  async upgradeToAnnual() {
    return this.post("/billing/upgrade-to-annual", {});
  },

  // ---------------------------------------------------------------------------
  // EPIC-055 / 55.11 — Écran abonnement ME (mois actif).
  //   - GET /billing/subscription-overview → agrégateur de la page
  //   - GET /billing/issuance-status → bannière suspension partageable
  //   - DELETE /billing/mandate → révocation côté ME (toast.confirm)
  // ---------------------------------------------------------------------------

  /**
   * Agrégateur de la page abonnement ME. Un seul round-trip.
   *
   * @returns {Promise<{
   *   subscription: {id: string, billingMode: string, status: string, includedActiveMonthsRemaining: number} | null,
   *   mandate: {status: string, ibanMasked: string|null, activatedAt: string|null, signatureUrl: string|null} | null,
   *   charges: Array<{yearMonth: string, amountHt: string, status: string, debitedAt: string|null, preNotificationSentAt: string|null}>,
   *   nextCharge: {yearMonth: string, amountHt: string, preNotificationDate: string|null} | null,
   *   creationFee: {amountHt: string, rsacAmountHt: string, creditRemaining: number, paidAt: string|null} | null,
   *   issuance: {suspended: boolean, reason: string|null}
   * }>}
   */
  async getSubscriptionOverview() {
    return this.get("/billing/subscription-overview");
  },

  /**
   * État d'émission de factures pour l'organisation courante (bannière
   * suspension partageable avec le wizard de facturation).
   *
   * @returns {Promise<{canIssue: boolean, suspensionReason: string|null, mandateStatus: string|null}>}
   */
  async getIssuanceStatus() {
    return this.get("/billing/issuance-status");
  },

  /**
   * Révoque le mandat SEPA B2B courant (action ME, après confirmation via
   * `toast.confirm`). Bloqué côté serveur si des charges ouvertes existent
   * (`pending` / `pre_notified` / `failed`) — retournera alors une 422
   * `mandate_revocation_blocked` avec `openChargesCount`.
   *
   * @returns {Promise<{success: true}>}
   */
  async revokeSubscriptionMandate() {
    return this.delete("/billing/mandate");
  },

  // ---------------------------------------------------------------------------
  // Encaissement Wero (EPIC-004 / 4.5) — paramètres organisation.
  //   - GET  /organization/settings/payment-provider
  //       → { status, credentialsConfigured, includeOnInvoices, includeOnQuotes,
  //           quoteDepositPct, autoInvoiceOnPayment }
  //   - PUT  /organization/settings/payment-provider
  //       body: { action: 'connect'|'disconnect'|'update', credentials?, … }
  //       → mêmes champs que GET.
  // Les credentials Wero ne sont JAMAIS retournés par GET (cf. spec §2.E
  // « jamais ré-exposés après saisie initiale ») — uniquement
  // `credentialsConfigured: bool`.
  // ---------------------------------------------------------------------------

  /**
   * Récupère l'état courant des paramètres d'encaissement Wero pour l'org.
   *
   * @returns {Promise<{
   *   status: 'not_configured'|'active'|'error',
   *   credentialsConfigured: boolean,
   *   includeOnInvoices: boolean,
   *   includeOnQuotes: boolean,
   *   quoteDepositPct: string,
   *   autoInvoiceOnPayment: boolean,
   * }>}
   */
  async getPaymentProviderSettings() {
    return this.get("/organization/settings/payment-provider");
  },

  /**
   * Met à jour les paramètres d'encaissement Wero (connect / disconnect / update).
   *
   * @param {object}   payload
   * @param {'connect'|'disconnect'|'update'} payload.action
   * @param {string}   [payload.credentials]          obligatoire pour `connect`
   * @param {boolean}  [payload.includeOnInvoices]
   * @param {boolean}  [payload.includeOnQuotes]
   * @param {number}   [payload.quoteDepositPct]      0..100
   * @param {boolean}  [payload.autoInvoiceOnPayment]
   */
  async updatePaymentProviderSettings(payload) {
    return this.put("/organization/settings/payment-provider", payload);
  },

  // ---------------------------------------------------------------------------
  // Paramètres §2.F — conditions de paiement (EPIC-004 / 4.6).
  //   - GET  /organization/settings
  //       → { paymentProvider: {...}, paymentConditions: {...} }
  //   - PUT  /organization/settings/payment-conditions
  //       body: { paymentDelayDefaultDays?, paymentConditionsText?,
  //               penaltyRateType?, penaltyRateValue?,
  //               badPayerMaxAvgDelay?, badPayerMaxLatePct? }
  //       → mêmes champs que GET (réponse complète, format structuré).
  //
  // Le frontend sauvegarde par `nm-edit-block` (un sous-ensemble de
  // champs par PUT) ; le backend ignore les champs absents (PATCH-friendly
  // sur un endpoint PUT — cf. SettingsService::updatePaymentConditions).
  // ---------------------------------------------------------------------------

  /**
   * Récupère l'état complet `organization_settings` exposé en V1
   * (encaissement Wero + conditions de paiement §2.F).
   *
   * @returns {Promise<{
   *   paymentProvider: object,
   *   paymentConditions: {
   *     paymentDelayDefaultDays: number,
   *     paymentConditionsText: string|null,
   *     penaltyRateType: 'multiple_taux_legal'|'fixe'|'bce_plus_pts',
   *     penaltyRateValue: number,
   *     badPayerMaxAvgDelay: number,
   *     badPayerMaxLatePct: number,
   *   },
   * }>}
   */
  async getSettings() {
    return this.get("/organization/settings");
  },

  /**
   * Met à jour tout ou partie des conditions de paiement §2.F.
   * Tous les champs sont optionnels — le frontend peut sauvegarder
   * par bloc (délai, pénalités, mention, seuils mauvais payeur).
   *
   * @param {object} payload
   * @param {number} [payload.paymentDelayDefaultDays] 0..90
   * @param {string|null} [payload.paymentConditionsText]
   * @param {'multiple_taux_legal'|'fixe'|'bce_plus_pts'} [payload.penaltyRateType]
   * @param {number} [payload.penaltyRateValue] 0..100
   * @param {number} [payload.badPayerMaxAvgDelay] 0..180
   * @param {number} [payload.badPayerMaxLatePct] 0..100
   */
  async updatePaymentConditions(payload) {
    return this.put("/organization/settings/payment-conditions", payload);
  },

  /**
   * EPIC-061 / 61.3 — toggles « bloc IBAN sur PDF ».
   * Met à jour 0/1/2 des 2 booléens dans `organization_settings`
   * (`include_iban_in_invoice_pdf`, `include_iban_in_quote_pdf`).
   * Au moins un champ doit être fourni sinon 422.
   *
   * @param {object} payload
   * @param {boolean} [payload.includeOnInvoices]
   * @param {boolean} [payload.includeOnQuotes]
   */
  async updateIbanOnPdf(payload) {
    return this.put("/organization/settings/iban-on-pdf", payload);
  },

  // ---------------------------------------------------------------------------
  // Numérotation paramétrable des factures §7.A (EPIC-041 / 41.2).
  //   - GET /numbering-pattern          → { data: NumberingPattern, lastNumber }
  //   - PUT /numbering-pattern          → { data: NumberingPattern } | 422
  //   - GET /numbering-pattern/preview  → { preview: string } | 422
  //
  // 1 pattern par organisation (ligne créée à l'activation, backfill 41.1).
  // `components` est un tableau ordonné de
  // 'year_short'|'year_full'|'month'|'counter' ; l'endpoint /preview l'attend
  // en CSV dans la query string. Le 422 (rupture de séquence, ou facture
  // récurrente active/en pause) porte un message FR affiché tel quel par le
  // controller `numbering-pattern`.
  // ---------------------------------------------------------------------------

  /**
   * Récupère le pattern de numérotation courant + le dernier numéro émis.
   *
   * @returns {Promise<{
   *   data: {
   *     id: string,
   *     organizationId: string,
   *     prefix: string,
   *     components: string[],
   *     separator: string,
   *     counterPadding: number,
   *     resetStrategy: 'annual'|'monthly'|'never',
   *     counterStartAt: number,
   *   },
   *   lastNumber: string|null,
   * }>}
   */
  async getNumberingPattern() {
    return this.get("/numbering-pattern");
  },

  /**
   * Met à jour le pattern de numérotation. 422 si la modification crée une
   * rupture de séquence (counterStartAt < dernier numéro émis) ou si une
   * facture récurrente active/en pause existe.
   *
   * @param {object} payload
   * @param {string}   payload.prefix
   * @param {string[]} payload.components       Ordre des composants.
   * @param {string}   payload.separator
   * @param {number}   payload.counterPadding   1..8
   * @param {'annual'|'monthly'|'never'} payload.resetStrategy
   * @param {number}   payload.counterStartAt   >= 1
   * @returns {Promise<{data: object}>}
   */
  async updateNumberingPattern(payload) {
    return this.put("/numbering-pattern", payload);
  },

  /**
   * Aperçu du prochain numéro pour un pattern hypothétique, sans le
   * consommer. `components` est sérialisé en CSV dans la query string.
   *
   * @param {object} pattern  Mêmes champs que updateNumberingPattern.
   * @returns {Promise<{preview: string}>}
   */
  async previewNumberingPattern(pattern) {
    return this.get("/numbering-pattern/preview", {
      prefix: pattern.prefix,
      components: pattern.components.join(","),
      separator: pattern.separator,
      counterPadding: String(pattern.counterPadding),
      resetStrategy: pattern.resetStrategy,
      counterStartAt: String(pattern.counterStartAt),
    });
  },

  // ---------------------------------------------------------------------------
  // Templates de relance §2.F (EPIC-004 / 4.7).
  //   - GET    /invoice-reminder-templates       → { templates: [...] } ordonnés par
  //                                         delay_days ASC.
  //   - POST   /invoice-reminder-templates       → 201 { id, organizationId,
  //                                         delayDays, …, status }
  //   - PUT    /invoice-reminder-templates/{id}  → 200 mêmes champs.
  //   - DELETE /invoice-reminder-templates/{id}  → 200 { archived: true }
  //                                         (soft-delete, pas DELETE physique).
  //
  // Le backend valide strictement les placeholders côté serveur (whitelist
  // fermée) — le frontend valide aussi côté client (defense in depth) mais
  // seul le serveur fait foi.
  //
  // À l'activation paiement initiale d'une org, 2 templates par défaut
  // (J+0 + J+7) sont seedés automatiquement par le backend
  // (`SubscriptionService::handlePaymentSucceeded` rétrofit). L'utilisateur
  // les retrouve donc pré-remplis dès la 1ère visite de cette page.
  // ---------------------------------------------------------------------------

  /**
   * Liste les templates de relance de l'organisation courante.
   *
   * Avec `includeArchived: true` (EPIC-099.23), inclut aussi les archivés —
   * utilisé par la vue archivés (`/relances-modeles.html?archived=1`) et par
   * le chargement direct d'un archivé sur `/relances-modeles-edition.html`.
   *
   * @param {object} [options]
   * @param {boolean} [options.includeArchived=false]
   * @returns {Promise<{templates: Array<object>}>}
   */
  async listReminderTemplates({ includeArchived = false } = {}) {
    const qs = includeArchived ? "?include_archived=1" : "";
    return this.get(`/invoice-reminder-templates${qs}`);
  },

  /**
   * Crée un nouveau template de relance.
   *
   * @param {object} payload
   * @param {number} payload.delayDays                0..180
   * @param {string} payload.titleImpayeB2b
   * @param {string} payload.textImpayeB2b
   * @param {string} payload.titlePartielB2b
   * @param {string} payload.textPartielB2b
   * @param {string} payload.titleImpayeB2c
   * @param {string} payload.textImpayeB2c
   * @param {string} payload.titlePartielB2c
   * @param {string} payload.textPartielB2c
   */
  async createReminderTemplate(payload) {
    return this.post("/invoice-reminder-templates", payload);
  },

  /**
   * Met à jour un template existant.
   *
   * @param {string} id   UUID du template.
   * @param {object} payload  Mêmes champs que createReminderTemplate.
   */
  async updateReminderTemplate(id, payload) {
    return this.put(`/invoice-reminder-templates/${id}`, payload);
  },

  /**
   * Archive (soft-delete) un template. Idempotent côté API.
   *
   * @param {string} id   UUID du template.
   */
  async archiveReminderTemplate(id) {
    return this.delete(`/invoice-reminder-templates/${id}`);
  },

  // ---------------------------------------------------------------------------
  // Wizard d'éligibilité aux aides — ACRE / ARE / ARCE (EPIC-005, §2.G).
  //   - GET  /aides/me      → état courant + valeur PASS + flags
  //                                wizardCompleted/wizardSkipped/dateLocked.
  //   - POST /aides/me      → upsert intégral. Publie
  //                                aides_wizard_completed.
  //                                422 si payload invalide. 409 si
  //                                acreStartDate ≠ creation_date sur
  //                                compte non-prospect (verrou D2).
  //   - POST /aides/me/skip → 204 + publication aides_wizard_skipped.
  //
  // RGPD : seule la conclusion d'éligibilité (acreStatus binaire) +
  // l'état ARE/ARCE sont persistés. Les motifs détaillés (RSA, AAH,
  // handicap, âge précis…) ne quittent jamais le navigateur — ils ne
  // sont même plus saisis : l'utilisateur déclare lui-même son
  // acreStatus via un select binaire.
  // ---------------------------------------------------------------------------

  /**
   * Récupère l'état courant des conclusions ACRE/ARE/ARCE de l'org.
   *
   * @returns {Promise<{
   *   acreStatus: string,
   *   acreStartDate: string|null,
   *   acreEndDate: string|null,
   *   acreRequestSentDate: string|null,
   *   unemploymentBenefitType: 'none'|'are'|'arce',
   *   areDailyNetAmount: string|null,
   *   areRightsOpeningDate: string|null,
   *   areTotalDaysGranted: number|null,
   *   areRemainingDaysAtMicroStart: number|null,
   *   arceTotalAmount: string|null,
   *   arceFirstPaymentDate: string|null,
   *   arceFirstPaymentAmount: string|null,
   *   arceSecondPaymentDueDate: string|null,
   *   arceSecondPaymentReceivedDate: string|null,
   *   arceSecondPaymentAmount: string|null,
   *   passCurrentValue: string,
   *   wizardCompleted: boolean,
   *   wizardSkipped: boolean,
   *   dateLocked: boolean,
   * }>}
   */
  async getAidesMe() {
    return this.get("/aides/me");
  },

  /**
   * Upsert intégral du wizard aides.
   *
   * @param {object} payload
   * @param {'eligible_not_requested'|'not_eligible'} payload.acreStatus
   * @param {string}   [payload.acreStartDate]              YYYY-MM-DD
   * @param {'none'|'are'|'arce'} payload.unemploymentBenefitType
   * @param {string}   [payload.areDailyNetAmount]          ARE only
   * @param {string}   [payload.areRightsOpeningDate]
   * @param {number}   [payload.areTotalDaysGranted]
   * @param {number}   [payload.areRemainingDaysAtMicroStart]
   * @param {string}   [payload.arceTotalAmount]            ARCE only
   * @param {string}   [payload.arceFirstPaymentDate]
   * @param {string}   [payload.arceFirstPaymentAmount]
   */
  async submitAides(payload) {
    return this.post("/aides/me", payload);
  },

  /**
   * Skip explicite (« Je passe pour l'instant »). Renvoie null (204).
   */
  async skipAides() {
    return this.post("/aides/me/skip", {});
  },

  // ---------------------------------------------------------------------------
  // EPIC-006 / 6.4 — Activités déclarées à l'INPI (§2.I).
  // Endpoints livrés en 6.3 (ActivityController) :
  //   - GET    /activities                       → { activities: [...] }
  //                                                    (active+archived)
  //   - POST   /activities                       → 201 { activity, formality }
  //                                                    (toujours secondaire active)
  //   - PATCH  /activities/{id}                  → 200 { activity, formality? }
  //                                                    (formality si champ déclaratif
  //                                                    modifié — sinon métadonnées
  //                                                    seules, pas de formalité INPI)
  //   - POST   /activities/{id}/archive          → 200 { activity }
  //                                                    (replacementPrincipalId requis
  //                                                    si l'activité archivée est
  //                                                    principale)
  //   - POST   /activities/{id}/set-principal    → 200 { swapped: [{...}, {...}] }
  //                                                    (swap atomique principale ↔
  //                                                    cible)
  //
  // Codes erreur métier (mappés en messages FR côté UI — D11 EPIC-006 :
  // « code APE » et non « NAF ») :
  //   - invalid_naf_format               (422)
  //   - validation_failed                (422)
  //   - replacement_principal_required   (400)
  //   - replacement_principal_invalid    (400)
  //   - activity_already_principal       (400)
  //   - activity_archived                (400)
  //   - activity_not_found               (404)
  // ---------------------------------------------------------------------------

  /**
   * Liste les activités de l'organisation courante. Toujours retourne actives +
   * archivées dans la même payload (le front filtre via toggle « archives »).
   *
   * @returns {Promise<{activities: Array<{
   *   id: string,
   *   role: 'principal'|'secondary',
   *   status: 'active'|'archived',
   *   category: 'bic_vente'|'bic_services'|'bnc',
   *   nafCode: string,
   *   description: string,
   *   startDate: string,
   *   archivedAt: string|null,
   *   location: string|null,
   *   regulatedCategory: string|null,
   *   isArtisanal: boolean,
   *   rcProNumber: string|null,
   *   rcProInsurer: string|null,
   *   rcProExpirationDate: string|null,
   *   decennaleNumber: string|null,
   *   decennaleInsurer: string|null,
   *   decennaleExpirationDate: string|null,
   *   pendingFormality: object|null,
   * }>}>}
   */
  async listActivities() {
    return this.get("/activities");
  },

  /**
   * Crée une nouvelle activité. Toujours créée en `secondary` `active`.
   * Une formalité INPI placeholder (`inpi_id` LOCAL-*) est créée côté backend.
   *
   * @param {object} payload
   * @param {'bic_vente'|'bic_services'|'bnc'} payload.category
   * @param {string} payload.nafCode                Format NN.NNL
   * @param {string} payload.description
   * @param {string} payload.startDate              YYYY-MM-DD
   * @param {string|null} [payload.location]
   * @param {string|null} [payload.regulatedCategory]
   * @param {boolean} [payload.isArtisanal]
   * @param {string|null} [payload.rcProNumber]
   * @param {string|null} [payload.rcProInsurer]
   * @param {string|null} [payload.rcProExpirationDate]
   * @param {string|null} [payload.decennaleNumber]
   * @param {string|null} [payload.decennaleInsurer]
   * @param {string|null} [payload.decennaleExpirationDate]
   */
  async createActivity(payload) {
    return this.post("/activities", payload);
  },

  /**
   * Met à jour une activité. Champs déclaratifs (description, nafCode, location,
   * regulatedCategory, isArtisanal) → formalité INPI créée. Métadonnées seules
   * (rcPro*, decennale*) → pas de formalité.
   *
   * @param {string} id
   * @param {object} payload  Mêmes champs que createActivity (tous optionnels).
   */
  async updateActivity(id, payload) {
    return this.patch(`/activities/${encodeURIComponent(id)}`, payload);
  },

  /**
   * Archive une activité (soft-delete, status='archived'). Si l'activité ciblée
   * est principale, `replacementPrincipalId` est obligatoire (l'activité de
   * remplacement devient principale dans la même transaction atomique).
   *
   * @param {string} id
   * @param {string|null} [replacementPrincipalId]
   */
  async archiveActivity(id, replacementPrincipalId = null) {
    return this.post(
      `/activities/${encodeURIComponent(id)}/archive`,
      replacementPrincipalId ? { replacementPrincipalId } : {},
    );
  },

  /**
   * Désigne l'activité {id} comme principale et bascule l'actuelle principale en
   * secondaire (swap atomique). 400 si l'activité ciblée est déjà principale ou
   * archivée.
   *
   * @param {string} id
   */
  async setPrincipalActivity(id) {
    return this.post(`/activities/${encodeURIComponent(id)}/set-principal`, {});
  },

  // ---------------------------------------------------------------------------
  // EPIC-007 / 7.2 — Médiation B2C & CGV (§2.J).
  //   - GET /organization/profile
  //       → 200 { bank: {...}, mediator: {...}, cgv: {...} }
  //   - PUT /organization/profile/mediator
  //       body: { name?, postalAddress?, website?, contactEmail? }
  //       → 200 { mediator: {...}, cgv: {...} } (état complet rechargé)
  //   - PUT /organization/profile/cgv
  //       body: { text?, attachToDocuments? }
  //       → 200 { mediator: {...}, cgv: {...} }
  //
  // Tous les champs sont optionnels — un champ omis n'est pas modifié,
  // un champ explicitement à null ou "" repasse la colonne à NULL.
  // ---------------------------------------------------------------------------

  /**
   * Charge l'état courant des blocs profil (bank, mediator, cgv) pour
   * /profil-entreprise.html. Renvoie des valeurs par défaut tant que
   * l'org n'a rien saisi (jamais 404).
   *
   * @returns {Promise<{
   *   bank: {dedicatedBankAccountStatus: string, alert: object|null},
   *   mediator: {name: ?string, postalAddress: ?string, website: ?string, contactEmail: ?string},
   *   cgv: {text: ?string, updatedAt: ?string, attachToDocuments: boolean}
   * }>}
   */
  async getOrganizationProfile() {
    return this.get("/organization/profile");
  },

  /**
   * Met à jour les coordonnées du médiateur consommateur.
   *
   * @param {object} payload
   * @param {string|null} [payload.name]
   * @param {string|null} [payload.postalAddress]
   * @param {string|null} [payload.website]
   * @param {string|null} [payload.contactEmail]
   * @returns {Promise<{mediator: object, cgv: object}>}
   */
  async updateMediator(payload) {
    return this.put("/organization/profile/mediator", payload);
  },

  /**
   * Met à jour les CGV B2C (texte + flag « joindre aux documents »).
   *
   * @param {object} payload
   * @param {string|null} [payload.text]
   * @param {boolean} [payload.attachToDocuments]
   * @returns {Promise<{mediator: object, cgv: object}>}
   */
  async updateCgv(payload) {
    return this.put("/organization/profile/cgv", payload);
  },

  // ---------------------------------------------------------------------------
  // EPIC-007 / 7.3 — Banque dédiée (§16.D).
  //   - PUT /organization/profile/bank
  //       body: { dedicatedBankAccountStatus: 'yes'|'no'|'not_yet'|'unknown' }
  //       → 200 { dedicatedBankAccountStatus, alert: {level,message,source}|null }
  // ---------------------------------------------------------------------------

  /**
   * Met à jour le statut « compte bancaire dédié » de l'organisation.
   *
   * @param {'yes'|'no'|'not_yet'|'unknown'} dedicatedBankAccountStatus
   * @returns {Promise<{dedicatedBankAccountStatus: string, alert: object|null}>}
   */
  async updateBankStatus(dedicatedBankAccountStatus) {
    return this.put("/organization/profile/bank", { dedicatedBankAccountStatus });
  },

  // ---------------------------------------------------------------------------
  // EPIC-099 / 99.15 — Identité légale + Coordonnées professionnelles (§2.F).
  //   - PUT /organization/profile/identity
  //       body: { legalName?, commercialName?, rcsCity?, rmDepartment? }
  //       → 200 { identity: { legalName, commercialName, rcsCity, rmDepartment } }
  //   - PUT /organization/profile/contact
  //       body: { addressLine1?, addressPostalCode?, addressCity?, phone?, emailPro? }
  //       → 200 { contact: { addressLine1, addressPostalCode, addressCity, phone, emailPro } }
  // ---------------------------------------------------------------------------

  /**
   * Met à jour l'identité légale (raison sociale, dénomination commerciale,
   * mentions RCS/RM).
   *
   * @param {object} payload
   * @param {string} [payload.legalName]            — non-vide si fourni.
   * @param {string|null} [payload.commercialName]
   * @param {string|null} [payload.rcsCity]
   * @param {string|null} [payload.rmDepartment]
   * @returns {Promise<{identity: {legalName: ?string, commercialName: ?string, rcsCity: ?string, rmDepartment: ?string}}>}
   */
  async updateOrganizationIdentity(payload) {
    return this.put("/organization/profile/identity", payload);
  },

  /**
   * Met à jour les coordonnées professionnelles (adresse, téléphone, email).
   *
   * @param {object} payload
   * @param {string|null} [payload.addressLine1]
   * @param {string|null} [payload.addressPostalCode]
   * @param {string|null} [payload.addressCity]
   * @param {string|null} [payload.phone]
   * @param {string|null} [payload.emailPro]
   * @returns {Promise<{contact: {addressLine1: ?string, addressPostalCode: ?string, addressCity: ?string, phone: ?string, emailPro: ?string}}>}
   */
  async updateOrganizationContact(payload) {
    return this.put("/organization/profile/contact", payload);
  },

  // ---------------------------------------------------------------------------
  // EPIC-099 / 99.73 — Onglet « Identité » de /profil-utilisateur.html (§2f).
  // État civil + adresse personnelle de l'utilisateur connecté, stockés sur
  // `users` (distincts de l'identité société ci-dessus).
  //   - GET /me/profile/identity
  //       → 200 { civilStatus, address, source: 'user'|'inpi_draft'|'empty' }
  //   - PUT /me/profile/civil-status
  //       body: { civility?, firstName, birthLastName, usageName?, birthDate, birthPlace?, birthCountry? }
  //       → 200 { civilStatus }
  //   - PUT /me/profile/personal-address
  //       body: { line1, postalCode, city, country? }
  //       → 200 { address }
  // ---------------------------------------------------------------------------

  /**
   * Charge l'état civil + l'adresse personnelle de l'utilisateur. Quand les
   * colonnes `users` sont vides, `source: 'inpi_draft'` indique des valeurs
   * pré-remplies depuis la formalité INPI de création (suggestion éditable).
   *
   * @returns {Promise<{civilStatus: object, address: object, source: string}>}
   */
  async getIdentity() {
    return this.get("/me/profile/identity");
  },

  /**
   * Met à jour l'état civil (write whitelisté sur `users`).
   *
   * @param {object} payload
   * @param {string|null} [payload.civility]   — 'madame'|'monsieur'|null.
   * @param {string} payload.firstName
   * @param {string} payload.birthLastName
   * @param {string|null} [payload.usageName]
   * @param {string} payload.birthDate          — YYYY-MM-DD.
   * @param {string|null} [payload.birthPlace]
   * @param {string|null} [payload.birthCountry] — ISO 2 lettres.
   * @returns {Promise<{civilStatus: object}>}
   */
  async updateCivilStatus(payload) {
    return this.put("/me/profile/civil-status", payload);
  },

  /**
   * Met à jour l'adresse personnelle (write whitelisté sur `users`).
   *
   * @param {object} payload
   * @param {string} payload.line1
   * @param {string} payload.postalCode — CP français 5 chiffres.
   * @param {string} payload.city
   * @param {string|null} [payload.country] — ISO 2 lettres, défaut FR.
   * @returns {Promise<{address: object}>}
   */
  async updatePersonalAddress(payload) {
    return this.put("/me/profile/personal-address", payload);
  },

  /**
   * EPIC-099 / 99.72 §2.F — Bascule l'option versement libératoire de l'IR
   * (`has_vl_option`, globale à l'org). Le serveur applique la règle 1×/an
   * et l'éligibilité RFR N-2. Renvoie le bloc `fiscal` relu.
   *
   * @param {object} payload
   * @param {boolean} payload.hasVlOption
   * @returns {Promise<{fiscal: object}>}
   */
  async updateFiscalOptions(payload) {
    return this.put("/organization/profile/fiscal-options", payload);
  },

  /**
   * EPIC-099 / 99.72 §2.F — Récupère à la demande la périodicité de
   * déclaration URSSAF (API020) et resynchronise le statut du mandat de
   * tierce déclaration. Renvoie le bloc `fiscal` relu.
   *
   * @returns {Promise<{fiscal: object}>}
   */
  async refreshUrssafPeriodicity() {
    return this.post("/organization/urssaf/refresh-periodicity", {});
  },

  // ---------------------------------------------------------------------------
  // EPIC-031 / 31.2 — Hub pilotage (§16).
  //   - GET /pilotage/hub
  //       → 200 {
  //           dedicatedBankAccountStatus,
  //           bankAlert: {level,message,source}|null,
  //           concentrationSummary: {topClientName, sharePercent, level, isEmpty},
  //           tjmSummary: {targetTjm, currency, isDraft},
  //           trajectorySummary: {gapPct, targetHt, currency, isEmpty},
  //           recentEvents: [{id, eventType, direction, detectedAt, data, ...}]
  //         }
  // ---------------------------------------------------------------------------

  /**
   * Charge le hub /pilotage : statut compte bancaire dédié + alerte
   * §16.D, synthèse live des 3 sous-modules (concentration / TJM /
   * trajectoire CA §16.E — la trésorerie §16.A a été retirée du hub),
   * 10 derniers pilotage_event.
   *
   * @returns {Promise<{
   *   dedicatedBankAccountStatus: string,
   *   bankAlert: object|null,
   *   concentrationSummary: object,
   *   tjmSummary: object,
   *   trajectorySummary: object,
   *   recentEvents: Array<object>
   * }>}
   */
  async getPilotageHub() {
    return this.get("/pilotage/hub");
  },

  /**
   * Charge les mouvements financiers de la période (EPIC-066 / 66.6, §31).
   * Vue calculée à chaque appel — aucun cache côté serveur. Le preset par
   * défaut (cadence URSSAF de l'org) est résolu côté serveur quand
   * `period` est absent ; la réponse renvoie le preset effectif dans
   * `period.preset`.
   *
   * Pas de filtre client/catégorie : Finances est une vue de flux globale
   * (décision EPIC-066 #7).
   *
   * @param {object} [params]
   * @param {string} [params.period] preset (`ecoule_*`/`encours_*`/`avenir_*`/`perso`)
   * @param {string} [params.from] date ISO `Y-m-d` (requis pour `perso`)
   * @param {string} [params.to] date ISO `Y-m-d` (requis pour `perso`)
   * @returns {Promise<{
   *   period: {preset: string, from: string, to: string},
   *   overdue: {amount: number, invoiceCount: number, oldestDays: number},
   *   cards: {
   *     realized: {in: number, out: number, net: number}|null,
   *     future: {in: number, out: number, net: number}|null,
   *     net: {amount: number, isAsymmetric: boolean}|null
   *   },
   *   lines: Array<{date: string, label: string, amount: number,
   *                 category: "encaisse"|"a_encaisser"|"paye"|"a_payer",
   *                 linkUrl: string}>,
   *   accrual: object|null
   * }>}
   */
  async getFinances(params = {}) {
    const query = {};
    if (params.period) query.period = params.period;
    if (params.from) query.from = params.from;
    if (params.to) query.to = params.to;
    return this.get("/finances", query);
  },

  /**
   * Charge la concentration client Pareto 12 mois glissants (EPIC-031 /
   * 31.4, §16.B). Calculée à chaque appel — l'effet de bord
   * `pilotage_event` (publication des franchissements de palier) est
   * idempotent côté DB (index unique partiel).
   *
   * @returns {Promise<{
   *   windowDays: number,
   *   totalCaHt: number,
   *   topClient: {clientId: string, name: string, caHt: number,
   *               percent: number, palier: string|null}|null,
   *   clients: Array<{clientId: string, name: string, caHt: number,
   *                   percent: number, palier: string|null}>,
   *   maxPalier: string|null,
   *   hasBnc: boolean,
   *   recentEvents: Array<object>,
   * }>}
   */
  async getPilotageConcentration() {
    return this.get("/pilotage/concentration");
  },

  // ---------------------------------------------------------------------------
  // EPIC-031 / 31.5 — Calculateur TJM 3 niveaux (§16.C).
  //   - GET /pilotage/tjm  → profil persisté (ou brouillon
  //     pré-rempli si aucun profil n'existe) + calcul des 3 niveaux
  //     (minimum / cible / optimal).
  //   - PUT /pilotage/tjm  → upsert atomique du profil
  //     (201 si premier upsert, 200 sinon).
  // ---------------------------------------------------------------------------

  /**
   * Charge le profil TJM + le calcul des 3 niveaux.
   *
   * @returns {Promise<{
   *   profile: object,
   *   isDraft: boolean,
   *   results: {
   *     minimum: object,
   *     target: object,
   *     optimal: object,
   *   },
   * }>}
   */
  async getPilotageTjm() {
    return this.get("/pilotage/tjm");
  },

  /**
   * Upsert atomique du profil TJM. Le serveur retourne le profil
   * rechargé et les 3 niveaux recalculés.
   *
   * @param {object} payload
   * @returns {Promise<{
   *   profile: object,
   *   isDraft: boolean,
   *   results: object,
   * }>}
   */
  async updatePilotageTjm(payload) {
    return this.put("/pilotage/tjm", payload);
  },

  // ---------------------------------------------------------------------------
  // EPIC-024 / 24.3 + 24.5 — CFE (Cotisation Foncière des Entreprises, §13).
  //   - GET   /cfe/overview   → état courant + flags d'éligibilité (+ currentYear, 24.5/D3).
  //   - PATCH /cfe/settings   → mise à jour partielle des settings CFE.
  //   - POST  /cfe/mark-paid  → CTA « J'ai payé » (Idempotency-Key UUID v4 obligatoire).
  // ---------------------------------------------------------------------------

  /**
   * Charge l'état CFE courant de l'organisation.
   *
   * @returns {Promise<{
   *   firstYearExonerationActive: boolean,
   *   cfeFirstYearExonerationExtendedEnabled: boolean,
   *   alertKind: string,
   *   nextDeadline: {date: string, kind: string}|null,
   *   cfeAmountLastYear: string|null,
   *   cfePaidForYear: number|null,
   *   creationDate: string|null,
   *   currentYear: number
   * }>}
   */
  async getCfeOverview() {
    return this.get("/cfe/overview");
  },

  /**
   * Met à jour les settings CFE (PATCH partiel). Champs optionnels.
   *
   * @param {object} payload
   * @param {number|null} [payload.cfeAmountLastYear]
   * @param {number|null} [payload.cfePaidForYear]
   * @param {boolean} [payload.cfeFirstYearExonerationExtendedEnabled]
   * @returns {Promise<object>} Shape `overview` mise à jour.
   */
  async updateCfeSettings(payload) {
    return this.patch("/cfe/settings", payload);
  },

  /**
   * CTA « J'ai payé » : positionne `cfe_paid_for_year` à l'année courante
   * (ou `year` fourni). Idempotent via header `Idempotency-Key` (UUID v4
   * régénéré à chaque clic, EPIC-024.5/D4).
   *
   * @param {string} idempotencyKey UUID v4.
   * @param {{amount: number, year?: number}} payload Body : `amount` obligatoire (montant payé, > 0, §99.78) ; `year` défaut = année courante serveur.
   * @returns {Promise<object>} Shape `overview` mise à jour.
   */
  async markCfePaid(idempotencyKey, payload = {}) {
    return this.post("/cfe/mark-paid", payload, { "Idempotency-Key": idempotencyKey });
  },

  // ---------------------------------------------------------------------------
  // EPIC-032 / 32.5 — Actions inline du dashboard payant §15.B :
  //   - PATCH /me/cfe-status                  → bouton « J'ai payé » sur
  //                                                 l'alerte CFE catégorie 4.
  //   - PATCH /me/profile/arce-second-payment → saisie inline de la
  //                                                 2ᵉ tranche ARCE (date + montant).
  // ---------------------------------------------------------------------------

  /**
   * @param {number} paidForYear
   * @returns {Promise<{cfePaidForYear: number}>}
   */
  async updateCfeStatus(paidForYear) {
    return this.patch("/me/cfe-status", { paidForYear });
  },

  /**
   * @param {string} receivedDate Format YYYY-MM-DD, date passée ou présente.
   * @param {string} amount        Montant décimal au format SQL ("1234.56").
   * @returns {Promise<{arceSecondPaymentReceivedDate: string, arceSecondPaymentAmount: string}>}
   */
  async updateArceSecondPayment(receivedDate, amount) {
    return this.patch("/me/profile/arce-second-payment", { receivedDate, amount });
  },

  // ---------------------------------------------------------------------------
  // EPIC-007 / 7.4 — Documents publics (dossier public).
  //   - GET    /organization/documents → { documents, defaultAttachments }
  //   - POST   /organization/documents (multipart) → 201 Document
  //   - PUT    /organization/documents/{id} → 200 Document
  //   - DELETE /organization/documents/{id} → 200 (soft-delete)
  //   - POST   /organization/default-attachments/invoices  body: {documentIds}
  //   - POST   /organization/default-attachments/quotes    body: {documentIds}
  //   - POST   /organization/regenerate-public-token       → { publicToken }
  // ---------------------------------------------------------------------------

  /**
   * Liste les documents publics actifs de l'organisation + sets par défaut.
   *
   * @returns {Promise<{documents: Array<object>, defaultAttachments: object}>}
   */
  async listDocuments() {
    return this.get("/organization/documents");
  },

  /**
   * Upload d'un document PDF (multipart/form-data).
   *
   * @param {File} file
   * @param {object} metadata
   * @param {string} metadata.name
   * @param {string} metadata.category
   * @param {string} [metadata.expirationDate] YYYY-MM-DD
   * @param {boolean} [metadata.isPublic]
   * @param {string} [metadata.linkedInsuranceActivityId]
   */
  async uploadDocument(file, metadata = {}) {
    return this.upload("/organization/documents", file, metadata);
  },

  /**
   * Met à jour les métadonnées d'un document.
   *
   * @param {string} id
   * @param {object} payload Mêmes champs que uploadDocument metadata (sans le file).
   */
  async updateDocument(id, payload) {
    return this.put(`/organization/documents/${encodeURIComponent(id)}`, payload);
  },

  /**
   * Soft-delete un document (status='archived').
   *
   * @param {string} id
   */
  async deleteDocument(id) {
    return this.delete(`/organization/documents/${encodeURIComponent(id)}`);
  },

  /**
   * Liste des devis et factures auxquels un document est attaché
   * (EPIC-034 / 34.4). Lecture seule, isolation tenant par 404.
   *
   * @param {string} id
   * @returns {Promise<{quotes: Array<object>, invoices: Array<object>}>}
   */
  async getDocumentAttachments(id) {
    return this.get(`/organization/documents/${encodeURIComponent(id)}/attachments`);
  },

  /**
   * Alertes d'expiration d'assurance (RC Pro + décennale) — EPIC-034 / 34.5.
   * Consommé par le bandeau Stimulus `insurance-expiration-banner` sur
   * `profil-entreprise.html` (onglet Assurances) et `tableau-de-bord-prospect.html`.
   *
   * @returns {Promise<{alerts: Array<{activityId: string, activityName: string, category: string, expirationDate: string, status: string, documentId: string|null, documentName: string|null}>}>}
   */
  async getInsuranceAlerts() {
    return this.get("/organization/insurance-alerts");
  },

  /**
   * Définit le set de documents joints par défaut aux factures émises.
   *
   * @param {string[]} documentIds
   */
  async setDefaultInvoiceAttachments(documentIds) {
    return this.post("/organization/default-attachments/invoices", { documentIds });
  },

  /**
   * Définit le set de documents joints par défaut aux devis.
   *
   * @param {string[]} documentIds
   */
  async setDefaultQuoteAttachments(documentIds) {
    return this.post("/organization/default-attachments/quotes", { documentIds });
  },

  /**
   * Régénère le token public du dossier (l'ancien lien devient 404).
   *
   * @returns {Promise<{publicToken: string}>}
   */
  async regeneratePublicToken() {
    return this.post("/organization/regenerate-public-token", {});
  },

  // ---------------------------------------------------------------------------
  // EPIC-007 / 7.5 — Fin de relation §25 (résiliation, cessation INPI,
  //                  réactivation, archivage gratuit A/B).
  //   - GET  /organization/lifecycle
  //       → { lifecycleStatus, resignationScheduledAt, events: [...] }
  //   - POST /organization/lifecycle/resignation
  //       body: { motive, motiveOther?, archiveChoice: 'online'|'export_only' }
  //       → 200 { lifecycleStatus, scheduledAt, archiveChoice }
  //   - POST /organization/lifecycle/resignation/cancel → 200
  //   - POST /organization/lifecycle/cessation-inpi
  //       body: { archiveChoice: 'online'|'export_only' }
  //       → 200 { lifecycleStatus, formality: {inpiId, signatureUrl}, archiveChoice }
  //   - POST /organization/lifecycle/reactivate → 200 { lifecycleStatus }
  // ---------------------------------------------------------------------------

  /**
   * État cycle de vie courant de l'organisation + 50 derniers events.
   *
   * @returns {Promise<{
   *   lifecycleStatus: string,
   *   resignationScheduledAt: string|null,
   *   events: Array<object>,
   * }>}
   */
  async getLifecycle() {
    return this.get("/organization/lifecycle");
  },

  /**
   * Initie une résiliation §25.B (motif + choix d'archivage gratuit).
   *
   * @param {object} payload
   * @param {string} payload.motive
   * @param {string} [payload.motiveOther]
   * @param {'online'|'export_only'} payload.archiveChoice
   *        `online` = archivage 10 ans (gratuit) ; `export_only` = export ZIP 90 j.
   */
  async initiateResignation(payload) {
    return this.post("/organization/lifecycle/resignation", payload);
  },

  /**
   * Annule la résiliation programmée (§25.B *Annulation*).
   */
  async cancelResignation() {
    return this.post("/organization/lifecycle/resignation/cancel", {});
  },

  /**
   * EPIC-037 / 37.4 — initie une cessation INPI §25.C : pousse le
   * brouillon au Guichet Unique INPI et retourne le deepLink de
   * signature qualifiée.
   *
   * @param {object} payload
   * @param {'online'|'export_only'} payload.archiveChoice
   */
  async initiateInpiCessation(payload) {
    return this.post("/organization/lifecycle/cessation-inpi", payload);
  },

  /**
   * Réactive une organisation §25.I (cas 1 ou cas 2).
   */
  async reactivateOrganization() {
    return this.post("/organization/lifecycle/reactivate", {});
  },

  // ---------------------------------------------------------------------------
  // EPIC-036 / 36.3 — RGPD §21 (export portabilité + suppression compte).
  //   - GET  /rgpd/export                   → ZIP binaire (Content-Disposition)
  //   - POST /rgpd/account-deletion-request → { status, supportEmail, mailtoUrl }
  //
  // EPIC-099 / 99.72 §2.F — la révocation du mandat URSSAF (API031) n'est
  // plus exposée côté ME (décision PO 2026-06-10) : le ME en fait la
  // demande par email au support, l'action est portée par le BO admin.
  //
  // Note : `email-change-request` est sur /auth/email-change-request
  // (livré EPIC-001 / 1.5).
  // ---------------------------------------------------------------------------

  /**
   * Demande de suppression de compte (RGPD art. 17). Publie l'event interne
   * (alerte ntfy) et renvoie un `mailtoUrl` à ouvrir côté client pour que
   * l'utilisateur formalise sa demande auprès du support.
   *
   * @param {object} [payload]
   * @param {string|null} [payload.reason]
   * @returns {Promise<{status: string, supportEmail: string, mailtoUrl: string}>}
   */
  async requestAccountDeletion(payload = {}) {
    return this.post("/rgpd/account-deletion-request", payload);
  },

  /**
   * Export RGPD (portabilité, art. 20) — récupère le ZIP de l'organisation
   * courante en streaming binaire. Renvoie un objet `{ blob, filename }` :
   * `filename` provient du header `Content-Disposition` si présent, sinon
   * fallback `notamicro-export-<YYYY-MM-DD>.zip`.
   *
   * Le 423 (génération déjà en cours) remonte en `APIError` standard pour
   * que l'appelant puisse afficher le message dédié.
   *
   * @returns {Promise<{blob: Blob, filename: string}>}
   */
  /**
   * URL absolue de l'export RGPD (téléchargement via `window.location.href`).
   * Absolue comme les autres builders de download : doit viser l'origine de
   * l'API, pas celle de la page.
   *
   * @returns {string}
   */
  rgpdExportUrl() {
    return `${API_CONFIG.baseURL}/rgpd/export`;
  },

  async rgpdExport() {
    const url = `${API_CONFIG.baseURL}/rgpd/export`;
    const response = await fetch(url, {
      method: "GET",
      credentials: "include",
      headers: { "Accept": "application/zip, application/json" },
    });
    if (!response.ok) {
      let data = null;
      const contentType = response.headers.get("content-type") || "";
      if (contentType.includes("application/json")) {
        try { data = await response.json(); } catch (_e) { /* noop */ }
      }
      if (response.status === 401) {
        redirectToLoginIfNeeded();
      }
      throw new APIError(extractErrorMessage(data, response), response.status, data);
    }
    const filename = parseFilenameFromContentDisposition(
      response.headers.get("content-disposition"),
    ) ?? defaultExportFilename();
    const blob = await response.blob();
    return { blob, filename };
  },

  /**
   * Demande de changement d'email — POST /auth/email-change-request
   * (livré EPIC-001 / 1.5). Ouvre un ticket support pour validation manuelle.
   *
   * @param {object} payload
   * @param {string} payload.newEmail
   * @param {string} [payload.reason]
   */
  async requestEmailChange(payload) {
    return this.post("/auth/email-change-request", payload);
  },

  // ---------------------------------------------------------------------------
  // EPIC-009 — Clients (CRUD + soft-delete + score paiement dérivé §3).
  //   - GET  /clients?page=&perPage=&q=&sort=&filter[status]=&filter[type]=
  //       → { data: [...], pagination: { page, perPage, total, totalPages } }
  //       paymentScore.badPayerBadge présent en liste (9.3 batch).
  //   - POST /clients/{id}/archive  → 200 (409 si déjà archivé)
  //   - POST /clients/{id}/restore  → 200 (409 si déjà actif)
  // ---------------------------------------------------------------------------

  /**
   * Liste paginée des clients de l'org courante. Les paramètres bracketés
   * `filter[status]` / `filter[type]` sont URL-encodés par URLSearchParams
   * (`%5B`/`%5D`) puis re-parsés en tableau côté PHP par `parse_str`.
   *
   * @param {object} [params]
   * @param {number} [params.page]
   * @param {number} [params.perPage]
   * @param {string} [params.q]
   * @param {string} [params.sort]         e.g. "legalName" ou "-legalName"
   * @param {string} [params["filter[status]"]]  "active" | "archived"
   * @param {string} [params["filter[type]"]]    "b2b" | "b2c" | "public_administration"
   * @returns {Promise<{data: Array<object>, pagination: {page:number, perPage:number, total:number, totalPages:number}}>}
   */
  async listClients(params = {}) {
    return this.get("/clients", params);
  },

  /**
   * Recherche globale transverse §15.E (EPIC-099 / 99.80). Renvoie un payload
   * groupé `{ query, totalCount, groups: [{ type, label, total, items }] }`.
   * Terme vide → `200` avec groupes vides.
   *
   * @param {string} q  Terme de recherche.
   */
  async search(q) {
    return this.get("/search", { q });
  },

  /**
   * Soft-delete d'un client (status → archived). 409 si déjà archivé.
   *
   * @param {string} id  UUID du client.
   */
  async archiveClient(id) {
    return this.post(`/clients/${encodeURIComponent(id)}/archive`, {});
  },

  /**
   * Restauration d'un client archivé (status → active). 409 si déjà actif.
   *
   * @param {string} id  UUID du client.
   */
  async restoreClient(id) {
    return this.post(`/clients/${encodeURIComponent(id)}/restore`, {});
  },

  /**
   * Détail d'un client (avec `paymentScore` calculé par 9.3
   * `ClientPaymentScoreService::compute`, et tableaux `contacts` /
   * `addresses` hydratés depuis les sous-tables).
   *
   * @param {string} id  UUID du client.
   * @returns {Promise<object>}
   */
  async getClient(id) {
    return this.get(`/clients/${encodeURIComponent(id)}`);
  },

  /**
   * Création d'un client (core uniquement). Les contacts et adresses
   * sont créés ensuite via les sous-routes dédiées (9.5 mode création).
   *
   * @param {object} payload
   * @param {string} payload.type       "b2b" | "b2c" | "public_administration"
   * @param {string} payload.legalName
   * @param {string} [payload.siret]
   * @param {string} [payload.vatIntracom]
   * @returns {Promise<object>}
   */
  async createClient(payload) {
    return this.post("/clients", payload);
  },

  /**
   * Modification partielle d'un client (core uniquement). Le `type` est
   * immuable post-création côté 9.2 — ne pas inclure dans le payload.
   *
   * @param {string} id
   * @param {object} payload
   * @returns {Promise<object>}
   */
  async updateClient(id, payload) {
    return this.patch(`/clients/${encodeURIComponent(id)}`, payload);
  },

  /**
   * Ajout d'un contact rattaché au client (sous-route 9.2). `email` est
   * obligatoire côté backend (NOT NULL). `isPrimary=true` bascule
   * transactionnellement l'éventuel contact primaire existant.
   *
   * @param {string} clientId
   * @param {object} payload
   * @param {string} payload.email
   * @param {string} [payload.firstName]
   * @param {string} [payload.lastName]
   * @param {string} [payload.phone]
   * @param {string} [payload.function]
   * @param {boolean} [payload.isPrimary]
   * @returns {Promise<object>}
   */
  async addClientContact(clientId, payload) {
    return this.post(`/clients/${encodeURIComponent(clientId)}/contacts`, payload);
  },

  async updateClientContact(clientId, contactId, payload) {
    return this.patch(
      `/clients/${encodeURIComponent(clientId)}/contacts/${encodeURIComponent(contactId)}`,
      payload,
    );
  },

  async archiveClientContact(clientId, contactId) {
    return this.post(
      `/clients/${encodeURIComponent(clientId)}/contacts/${encodeURIComponent(contactId)}/archive`,
      {},
    );
  },

  /**
   * Ajout d'une adresse rattachée au client. `type` ∈ `billing|shipping` ;
   * 1 seule `isDefault=true` active par couple (client, type) côté backend.
   *
   * @param {string} clientId
   * @param {object} payload
   * @param {"billing"|"shipping"} payload.type
   * @param {string} payload.line1
   * @param {string} [payload.line2]
   * @param {string} payload.postalCode
   * @param {string} payload.city
   * @param {string} payload.country     ISO 3166-1 alpha-2 (défaut FR)
   * @param {boolean} [payload.isDefault]
   * @returns {Promise<object>}
   */
  async addClientAddress(clientId, payload) {
    return this.post(`/clients/${encodeURIComponent(clientId)}/addresses`, payload);
  },

  async updateClientAddress(clientId, addressId, payload) {
    return this.patch(
      `/clients/${encodeURIComponent(clientId)}/addresses/${encodeURIComponent(addressId)}`,
      payload,
    );
  },

  async archiveClientAddress(clientId, addressId) {
    return this.post(
      `/clients/${encodeURIComponent(clientId)}/addresses/${encodeURIComponent(addressId)}/archive`,
      {},
    );
  },

  // ---------------------------------------------------------------------------
  // EPIC-010 — Articles (catalogue, FK activité §4).
  //   - GET /articles?page=&perPage=&q=&sort=&filter[activityId]=&filter[status]=
  //       → { data: [...], pagination: { page, perPage, total, totalPages } }
  //   - Soft-delete et restauration câblés en 10.4 sur la page détail.
  // ---------------------------------------------------------------------------

  /**
   * Liste paginée du catalogue d'articles de l'org courante. Les paramètres
   * bracketés `filter[activityId]` / `filter[status]` sont URL-encodés par
   * URLSearchParams (`%5B`/`%5D`) puis re-parsés en tableau côté PHP par
   * `parse_str`.
   *
   * Backend 10.2 :
   *  - `filter[status]` valeur unique (`active` ou `archived`) — défaut
   *    serveur `active`. Pas de combinaison « les deux » à ce stade.
   *  - `sort` whitelist : `label`, `createdAt`, `updatedAt` (préfixe `-`
   *    pour descendant). Autres champs → 422 `invalid_sort_field`.
   *
   * @param {object} [params]
   * @param {number} [params.page]
   * @param {number} [params.perPage]
   * @param {string} [params.q]                          recherche ILIKE sur `label`
   * @param {string} [params.sort]                       "label" / "-label" / "createdAt" / "-createdAt" / "updatedAt" / "-updatedAt"
   * @param {string} [params["filter[activityId]"]]      UUID d'une activité de l'org
   * @param {string} [params["filter[status]"]]          "active" | "archived"
   * @returns {Promise<{data: Array<object>, pagination: {page:number, perPage:number, total:number, totalPages:number}}>}
   */
  async listArticles(params = {}) {
    return this.get("/articles", params);
  },

  /**
   * Détail d'un article (page /articles/{id}, EPIC-010 / 10.4).
   *
   * @param {string} id  UUID de l'article.
   * @returns {Promise<object>}
   */
  async getArticle(id) {
    return this.get(`/articles/${encodeURIComponent(id)}`);
  },

  /**
   * Crée un article. Toujours créé `active`. `activityId` doit pointer vers
   * une activité `active` de l'organisation (422 sinon).
   *
   * @param {object} payload
   * @param {string} payload.label
   * @param {string} [payload.description]
   * @param {string} payload.unit
   * @param {string} payload.priceExclVat        Chaîne décimale (≥ 0, ≤ 2 décimales)
   * @param {string} payload.vatRate             "0" | "2.1" | "5.5" | "10" | "20"
   * @param {string} payload.activityId
   * @returns {Promise<object>}
   */
  async createArticle(payload) {
    return this.post("/articles", payload);
  },

  /**
   * Modification partielle d'un article. Refusée (422
   * `article_activity_archived`) si l'activité courante est archivée et
   * que le payload ne contient pas un `activityId` actif.
   *
   * @param {string} id
   * @param {object} payload  Champs partiels (mêmes que createArticle).
   * @returns {Promise<object>}
   */
  async updateArticle(id, payload) {
    return this.patch(`/articles/${encodeURIComponent(id)}`, payload);
  },

  /**
   * Soft-delete (status → archived). 422 `blocked_by_recurring_invoices`
   * si au moins une `recurring_invoice` `active`/`paused` référence
   * l'article (cf. ArticleService::archive). 409 si déjà archivé.
   *
   * @param {string} id
   */
  async archiveArticle(id) {
    return this.post(`/articles/${encodeURIComponent(id)}/archive`, {});
  },

  /**
   * Restauration d'un article archivé (status → active). 409 si déjà actif.
   *
   * @param {string} id
   */
  async restoreArticle(id) {
    return this.post(`/articles/${encodeURIComponent(id)}/restore`, {});
  },

  // ---------------------------------------------------------------------------
  // EPIC-008 / 8.7 — INPI Guichet Unique côté utilisateur final.
  //   Création (8.4) :
  //   - POST /inpi/creation/attachments  (multipart) → { attachment }
  //       body: file (PDF ≤ 10 MB) + category (InpiAttachmentType).
  //   - POST /inpi/creation/submit       (JSON)      → 201 { formality, subscription }
  //       subscription = { intentId, paymentLinkUrl, amountHt, cadence, parcours }.
  //   Suivi (8.4 list / 8.5 polling) :
  //   - GET  /inpi/formalities          → { formalities: [...] }
  //   - GET  /inpi/formalities/{id}     → { formality, attachments,
  //                                              events, regularizationRequests }
  //   Modifications (8.6) :
  //   - POST /inpi/modification/{id}/sign → { redirectUrl, formality }
  //       (push d'un brouillon LOCAL-* puis URL profonde de signature
  //        qualifiée procedures.inpi.fr).
  //   Régularisations (8.7 backend) :
  //   - POST /inpi/regularization/{id}/upload (multipart) → { attachment }
  //       body: file (PDF ≤ 10 MB) + requirementId (UUID).
  //   - POST /inpi/regularization/{id}/submit (JSON, body vide)
  //       → { formality } pour creation (ntfy admin pour ré-dépôt)
  //       → { formality, redirectUrl } pour modification (re-signature).
  //
  // Pattern : Notamicro reste le seul interlocuteur côté UI (vocabulaire
  // §0 CLAUDE.md — INPI / Guichet Unique / procedures.inpi.fr visibles ;
  // NotaFacto / Sweego invisibles).
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  // EPIC-012 — Devis (cycle, jalons, conversion 1-clic).
  //
  // Endpoints livrés en 12.2–12.5 ; 12.6 ne consomme que les routes existantes
  // (le contrôleur backend enrichit la réponse listing avec `clientLegalName`
  // pour la page liste — cf. QuoteController::index).
  //
  //  Listing :
  //   - GET /quotes?page=&perPage=&status=&from=&to=&q=&clientId=
  //       → { data: [{...}], pagination: {...} }
  //         data[i].clientLegalName ajouté par 12.6 (batch côté contrôleur).
  //  Détail :
  //   - GET /quotes/{id} → quote + events + linkedInvoices + isLocked +
  //     paymentProvider.{linkOpenedAt, paidAt} + advancePaymentInvoiceId.
  //  Mutations brouillon :
  //   - POST /quotes/draft                 → 201 quote (draft)
  //   - PATCH /quotes/{id}                 → 200 quote (updateDraft)
  //  Cycle :
  //   - POST /quotes/{id}/issue|send|accept|refuse|archive
  //  Acompte service d'encaissement :
  //   - POST /quotes/{id}/payment-provider/configure
  //  PDF :
  //   - GET  /quotes/{id}/preview-pdf        → binaire PDF
  //  Jalons + conversion :
  //   - GET  /quotes/{id}/milestones/preflight → { case, ... }
  //   - POST /quotes/{id}/milestones           → 201 { quote, milestoneInvoices }
  //   - POST /quotes/{id}/milestones/reset     → 200 quote
  //   - POST /quotes/{id}/convert-to-invoice   → 201 { quote, invoice }
  // ---------------------------------------------------------------------------

  /**
   * Liste paginée des devis de l'org courante.
   *
   * @param {object} [params]
   * @param {number} [params.page]
   * @param {number} [params.perPage]
   * @param {string} [params.q]              recherche libre (numéro ou client)
   * @param {string} [params.status]         valeur unique ou liste CSV
   * @param {string} [params.from]           YYYY-MM-DD (≥ date_created)
   * @param {string} [params.to]             YYYY-MM-DD (≤ date_created)
   * @param {string} [params.clientId]       UUID
   * @returns {Promise<{data: Array<object>, pagination: object}>}
   */
  async listQuotes(params = {}) {
    return this.get("/quotes", params);
  },

  async getQuote(id) {
    return this.get(`/quotes/${encodeURIComponent(id)}`);
  },

  /**
   * URL absolue de l'aperçu PDF (brouillon) d'un devis — à ouvrir dans
   * un onglet / un <a href>. Construite sur `API_CONFIG.baseURL` et non
   * sur `window.location.origin` : en dev le front est servi sur un
   * autre port que l'API PHP, sinon la requête tape le serveur statique
   * et renvoie 404. Cf. invoicePdfUrl().
   *
   * @param {string} id
   * @returns {string}
   */
  quotePreviewPdfUrl(id) {
    return `${API_CONFIG.baseURL}/quotes/${encodeURIComponent(id)}/preview-pdf`;
  },

  async createQuote(payload) {
    return this.post("/quotes/draft", payload);
  },

  async updateQuote(id, payload) {
    return this.patch(`/quotes/${encodeURIComponent(id)}`, payload);
  },

  async issueQuote(id) {
    return this.post(`/quotes/${encodeURIComponent(id)}/issue`, {});
  },

  async sendQuote(id, payload = {}) {
    return this.post(`/quotes/${encodeURIComponent(id)}/send`, payload);
  },

  async resendQuote(id, payload = {}) {
    return this.post(`/quotes/${encodeURIComponent(id)}/resend`, payload);
  },

  async acceptQuote(id) {
    return this.post(`/quotes/${encodeURIComponent(id)}/accept`, {});
  },

  async refuseQuote(id) {
    return this.post(`/quotes/${encodeURIComponent(id)}/refuse`, {});
  },

  async archiveQuote(id) {
    return this.post(`/quotes/${encodeURIComponent(id)}/archive`, {});
  },

  async configureQuotePaymentProvider(id, payload) {
    return this.post(`/quotes/${encodeURIComponent(id)}/payment-provider/configure`, payload);
  },

  async getQuoteMilestonesPreflight(id) {
    return this.get(`/quotes/${encodeURIComponent(id)}/milestones/preflight`);
  },

  async defineQuoteMilestones(id, payload) {
    return this.post(`/quotes/${encodeURIComponent(id)}/milestones`, payload);
  },

  async resetQuoteMilestones(id) {
    return this.post(`/quotes/${encodeURIComponent(id)}/milestones/reset`, {});
  },

  async convertQuoteToInvoice(id) {
    return this.post(`/quotes/${encodeURIComponent(id)}/convert-to-invoice`, {});
  },

  // ---------------------------------------------------------------------------
  // EPIC-056 / 56.4 — Acceptation publique des devis : actions ME.
  //  - POST /quotes/{id}/extend-validity {newValidityDate}  → 200 quote
  //  - POST /quotes/{id}/renew                              → 201 {quote}
  // ---------------------------------------------------------------------------

  async extendQuoteValidity(id, newValidityDate) {
    return this.post(`/quotes/${encodeURIComponent(id)}/extend-validity`, { newValidityDate });
  },

  async renewQuote(id) {
    return this.post(`/quotes/${encodeURIComponent(id)}/renew`, {});
  },

  // ---------------------------------------------------------------------------
  // EPIC-051 / 51.4 — Wizard de facturation depuis devis (§7.G).
  //  - GET    /quotes/{id}/invoices              → { invoices: [...] }
  //  - POST   /quotes/{id}/generate-invoices     → 201 { invoices: [...] }
  //  - POST   /quotes/{id}/invoices/milestones   → 201 { invoice }
  //  - DELETE /quotes/{id}/invoices/drafts       → 200 { deleted: N }
  // ---------------------------------------------------------------------------

  /**
   * Liste les factures du lot adossé au devis (drafts + émises +
   * annulées, hors archived). Tri chronologique.
   * @param {string} id quote UUID
   * @returns {Promise<{invoices: Array<object>}>}
   */
  async getQuoteInvoices(id) {
    return this.get(`/quotes/${encodeURIComponent(id)}/invoices`);
  },

  /**
   * Génération atomique du lot de brouillons (acompte / jalons / settlement)
   * selon les 4 combinaisons §7.G.
   *
   * EPIC-099/99.38 — chaque jalon porte une `milestoneNature` :
   *  - `'percentage'` (défaut) : `{label, amount?, percent?}` (XOR), ligne unique.
   *  - `'quantity'`             : `{label, lines: [{quoteLineIndex, quantity}]}`,
   *    reprise des lignes du devis aux quantités saisies (situation BTP).
   *
   * @param {string} id quote UUID
   * @param {object} payload
   * @param {{type: 'percent'|'amount', value: number}|null} payload.advance
   * @param {Array<{label: string, milestoneNature?: 'percentage'|'quantity',
   *                 amount?: number, percent?: number,
   *                 lines?: Array<{quoteLineIndex: number, quantity: number}>}>|null} payload.milestones
   * @returns {Promise<{invoices: Array<object>}>}
   */
  async generateQuoteInvoices(id, payload) {
    return this.post(
      `/quotes/${encodeURIComponent(id)}/generate-invoices`,
      payload,
    );
  },

  /**
   * Ajout post-émission d'un jalon supplémentaire. Soumis au garde
   * anti-dépassement. Le brouillon settlement (s'il existe) sera recalculé
   * automatiquement à la prochaine lecture.
   *
   * @param {string} id quote UUID
   * @param {{label?: string, amount?: number, percent?: number}} payload
   * @returns {Promise<{invoice: object}>}
   */
  async addQuoteMilestone(id, payload) {
    return this.post(
      `/quotes/${encodeURIComponent(id)}/invoices/milestones`,
      payload,
    );
  },

  /**
   * Archive en bulk tous les brouillons du lot (« Recommencer le découpage »).
   * Refusé (422 `reset_blocked_invoice_issued`) si au moins une facture du
   * lot est déjà émise.
   *
   * @param {string} id quote UUID
   * @returns {Promise<{deleted: number}>}
   */
  async resetQuoteDrafts(id) {
    return this.delete(`/quotes/${encodeURIComponent(id)}/invoices/drafts`);
  },

  // ---------------------------------------------------------------------------
  // EPIC-013 — Factures émises (cycle, paiements, relances).
  //
  // Endpoints livrés en 13.2–13.6 :
  //  Listing & cycle :
  //   - GET   /invoices                  (filtres status, kind, clientId,
  //                                           from, to, q, page, perPage)
  //   - POST  /invoices/draft            → 201 invoice
  //   - GET   /invoices/{id}             → invoice complet
  //   - PATCH /invoices/{id}             → updateDraft
  //   - POST  /invoices/{id}/issue       → draft → issued
  //   - POST  /invoices/{id}/cancel      body {reason?}
  //                                          → 201 { invoice, creditNote }
  //   - POST  /invoices/{id}/archive     → 200 invoice (idempotent)
  //   - POST  /invoices/{id}/resend      body {correctEmail?} → 200 invoice
  //   - GET   /invoices/{id}/pdf         → application/pdf (binaire,
  //                                            ouvrir via <a href="…"
  //                                            target="_blank">)
  //   - GET   /organizations/me/legal-mention-warnings → 200 { warnings: [...] }
  //  Paiements §7.I (EPIC-042) — désormais unifiés sous `payment_transaction` :
  //   - voir `api.payments.*` plus bas (list / get / create / update /
  //     archive / counterEntry / attach / dismiss / pending).
  //  Relances §7.E (13.6) :
  //   - GET   /reminders                 (filtres status, from, to,
  //                                           clientId, invoiceId, page,
  //                                           pageSize) → { items, pagination }
  //   - GET   /invoices/{id}/reminders   → { timeline: [...] }
  //   - PATCH /invoices/{id}/reminders/toggle  body {enabled: bool}
  // ---------------------------------------------------------------------------

  /**
   * Liste paginée des factures émises de l'organisation courante.
   *
   * @param {object} [params]
   * @param {number} [params.page]
   * @param {number} [params.perPage]
   * @param {string} [params.q]              recherche libre (n° ou client)
   * @param {string} [params.status]         draft|issued|sent|paid|partially_paid|overdue|cancelled|archived
   * @param {string} [params.kind]           standard|advance_payment|credit_note
   * @param {string} [params.from]           YYYY-MM-DD (≥ date_emission)
   * @param {string} [params.to]             YYYY-MM-DD (≤ date_emission)
   * @param {string} [params.clientId]       UUID
   * @returns {Promise<{data: Array<object>, pagination: object}>}
   */
  async listInvoices(params = {}) {
    return this.get("/invoices", params);
  },

  async getInvoice(id) {
    return this.get(`/invoices/${encodeURIComponent(id)}`);
  },

  /**
   * Crée un brouillon (POST /invoices/draft).
   *
   * @param {object} payload
   * @param {string} payload.clientId
   * @param {'standard'|'advance_payment'|'credit_note'} [payload.kind]
   * @param {string} [payload.dateEmission]   YYYY-MM-DD
   * @param {string} [payload.dateDue]        YYYY-MM-DD
   * @param {Array<object>} [payload.lines]
   * @param {string} [payload.sourceInvoiceId]  obligatoire si kind=credit_note
   * @returns {Promise<object>}
   */
  async createInvoiceDraft(payload) {
    return this.post("/invoices/draft", payload);
  },

  /**
   * Patch partiel d'un draft. Refusé si la facture n'est plus en `draft`
   * (409 invoice_not_draft).
   *
   * @param {string} id
   * @param {object} payload
   * @returns {Promise<object>}
   */
  async updateInvoiceDraft(id, payload) {
    return this.patch(`/invoices/${encodeURIComponent(id)}`, payload);
  },

  /**
   * Émet la facture (draft → issued). 422 si mentions légales RCS/RM
   * manquantes (code `business_rule_violation`, details.code='legal_mention_missing').
   */
  async issueInvoice(id) {
    return this.post(`/invoices/${encodeURIComponent(id)}/issue`, {});
  },

  /**
   * Annule la facture et crée un avoir intégral lié.
   *
   * @param {string} id
   * @param {object} [payload]
   * @param {string} [payload.reason]   raison (≤500 chars)
   * @returns {Promise<{invoice: object, creditNote: object}>}
   */
  async cancelInvoice(id, payload = {}) {
    return this.post(`/invoices/${encodeURIComponent(id)}/cancel`, payload);
  },

  /**
   * Programme l'émission automatique d'un brouillon (draft → scheduled).
   * 422 si date hors bornes (J+1 ≤ d ≤ fin du mois suivant), si la facture
   * est de type settlement, ou si elle dépasserait le devis associé.
   * EPIC-053 / 53.3.
   *
   * @param {string} id
   * @param {string} toIssueOn   format YYYY-MM-DD
   */
  async scheduleInvoice(id, toIssueOn) {
    return this.post(`/invoices/${encodeURIComponent(id)}/schedule`, { toIssueOn });
  },

  /**
   * Annule la programmation (scheduled → draft). EPIC-053 / 53.3.
   */
  async unscheduleInvoice(id) {
    return this.post(`/invoices/${encodeURIComponent(id)}/unschedule`, {});
  },

  async archiveInvoice(id) {
    return this.post(`/invoices/${encodeURIComponent(id)}/archive`, {});
  },

  /**
   * Renvoie une facture B2C en échec (bouton « Renvoyer »). B2B no-op
   * tant que la PA SuperPDP n'est pas activée (13.B2). Body optionnel
   * `correctEmail` met à jour l'email du contact primaire avant l'envoi.
   *
   * @param {string} id
   * @param {object} [payload]
   * @param {string} [payload.correctEmail]
   */
  async resendInvoice(id, payload = {}) {
    return this.post(`/invoices/${encodeURIComponent(id)}/resend`, payload);
  },

  /**
   * URL du PDF de la facture. Ouvert en nouvel onglet par le bouton
   * « Télécharger PDF » de la fiche.
   *
   * @param {string} id
   * @returns {string}
   */
  invoicePdfUrl(id) {
    return `${API_CONFIG.baseURL}/invoices/${encodeURIComponent(id)}/pdf`;
  },

  /**
   * Mentions légales RCS/RM manquantes (bandeau pédagogique liste +
   * dashboard).
   *
   * @returns {Promise<{warnings: Array<object>}>}
   */
  async getLegalMentionWarnings() {
    return this.get("/organizations/me/legal-mention-warnings");
  },

  // ---------- Factures reçues §8 (EPIC-017 / 17.5) ----------

  /**
   * Liste paginée des factures reçues — déclenche côté backend le
   * `syncFromGateway` (polling PA SuperPDP) avant retour.
   *
   * @param {object} params
   * @param {number} [params.page]
   * @param {number} [params.perPage]
   * @param {string} [params.sort]                   ex: `-issuedAt`, `amountInclVat`
   * @param {string} [params['filter[status]']]
   * @param {string} [params['filter[validationStatus]']]
   * @param {string} [params['filter[origin]']]      `pdp` | `upload`
   * @param {string} [params['filter[period]']]      `YYYY-MM,YYYY-MM`
   * @param {string} [params['filter[supplierSiret]']]
   * @returns {Promise<{data: Array<object>, pagination: object}>}
   */
  async listPurchaseInvoices(params = {}) {
    return this.get("/purchase-invoices", params);
  },

  /**
   * Upload manuel d'un PDF de facture reçue (canal `external` §8 — OCR +
   * archivage chez la PA via la passerelle). 503 quand la passerelle est
   * indisponible ; 422 si validation MIME/taille côté serveur.
   *
   * @param {File} file
   * @param {string} idempotencyKey   UUID v4 (régénérée à chaque tentative)
   * @returns {Promise<object>}
   */
  async uploadPurchaseInvoice(file, idempotencyKey) {
    return this.upload(
      "/purchase-invoices/upload",
      file,
      {},
      "pdf",
      { "Idempotency-Key": idempotencyKey },
    );
  },

  /**
   * Détail d'une facture reçue (§8). Pour le canal `pdp`, le backend
   * remonte les champs dynamiques depuis la passerelle PA. Le payload
   * inclut la timeline `events` (DESC happened_at).
   *
   * @param {string} id
   * @returns {Promise<object>}
   */
  async getPurchaseInvoice(id) {
    return this.get(`/purchase-invoices/${encodeURIComponent(id)}`);
  },

  /**
   * Patch partiel sur une facture reçue : validation
   * (`{validationStatus: "validated", expenseCategory, notes?}`),
   * mise à jour catégorie/notes, ou transition de statut.
   *
   * @param {string} id
   * @param {object} payload
   * @returns {Promise<object>}
   */
  async patchPurchaseInvoice(id, payload) {
    return this.patch(`/purchase-invoices/${encodeURIComponent(id)}`, payload);
  },

  // ---------- Paiements §7.C ----------
  //
  // Les anciens helpers (`listInvoicePayments`, `createInvoicePayment`,
  // `updateInvoicePayment`, `archiveInvoicePayment`) qui tapaient
  // /v1/invoices/{id}/payments/* ont été supprimés en EPIC-042 / 42.5 :
  // le modèle de paiement est désormais unifié sous `payment_transaction`
  // (§7.I), et toutes les opérations passent par le namespace `api.payments`
  // défini plus bas. Filtrer par facture via `api.payments.list({invoiceId})`,
  // archiver via `api.payments.archive(id)`, etc.

  // ---------- Relances §7.E ----------

  /**
   * Page Relances org. Pagination 25/page par défaut (max 100).
   *
   * @param {object} [params]
   * @param {string} [params.status]    scheduled|sent|failed|cancelled
   * @param {string} [params.from]      YYYY-MM-DD
   * @param {string} [params.to]        YYYY-MM-DD
   * @param {string} [params.clientId]
   * @param {string} [params.invoiceId]
   * @param {number} [params.page]
   * @param {number} [params.pageSize]
   * @returns {Promise<{items: Array<object>, pagination: object}>}
   */
  async listReminders(params = {}) {
    return this.get("/reminders", params);
  },

  /**
   * Timeline 0–180j (passées + prévues) pour une facture.
   *
   * @param {string} invoiceId
   * @returns {Promise<{timeline: Array<object>}>}
   */
  async getInvoiceReminders(invoiceId) {
    return this.get(`/invoices/${encodeURIComponent(invoiceId)}/reminders`);
  },

  /**
   * Toggle `reminders_enabled` sur une facture (PATCH).
   *
   * @param {string} invoiceId
   * @param {boolean} enabled
   * @returns {Promise<{invoice: object, reminders: Array<object>}>}
   */
  async toggleInvoiceReminders(invoiceId, enabled) {
    return this.patch(
      `/invoices/${encodeURIComponent(invoiceId)}/reminders/toggle`,
      { enabled },
    );
  },

  /**
   * EPIC-016 / 16.3 — Liste paginée du livre des recettes (§7.H.C).
   *
   * @param {object} params
   * @param {number} params.year         Année civile (obligatoire).
   * @param {number} [params.month]      Mois (1-12).
   * @param {string} [params.monthStatus] all|confirmed|unconfirmed.
   * @param {number} [params.page]
   * @param {number} [params.perPage]
   * @returns {Promise<{data: Array<object>, pagination: object, alerts: object, closures: Array<object>}>}
   */
  async listRevenueRegister(params = {}) {
    return this.get("/revenue/register", params);
  },

  /**
   * EPIC-099 / 99.27 — Détail d'un mois civil du livre des recettes
   * (§7.H.C.2). Endpoint distinct de l'index annuel (`/revenue/register?year=N`).
   *
   * Réponse `{ year, month, entries, totals, meta: { isVatSubject, sealed,
   * confirmed, hashStored, hashCurrent, hashDiverged, retroactiveCount,
   * preConfirmWindow, confirmEligible, ... } }`.
   *
   * @param {number} year
   * @param {number} month
   */
  async getRevenueRegisterMonthDetail(year, month) {
    const y = String(year);
    const m = String(month).padStart(2, "0");
    return this.get(`/revenue/register/${y}-${m}`);
  },

  /**
   * EPIC-016 / 16.4 — Création d'une entrée manuelle scellée du livre des
   * recettes (§7.H.B + §7.H.D, BIC vente uniquement).
   *
   * Si `attachment` est un `File`, l'envoi est `multipart/form-data` via
   * `api.upload()` (clé `attachment`). Sinon, JSON via `api.post()`.
   *
   * @param {object} payload
   * @param {string} payload.entryDate         YYYY-MM-DD, ≤ aujourd'hui
   * @param {string} payload.description       Nature de la recette
   * @param {string} payload.activityId        UUID activité BIC vente active
   * @param {string} payload.amountInclVat     TTC, strictement < 76 €
   * @param {string} payload.paymentMethod     especes|cb|cheque|virement|autre
   * @param {string} [payload.amountExclVat]   HT (ignoré si franchise)
   * @param {string} [payload.vatRate]         % (0|5.5|10|20, ignoré si franchise)
   * @param {string} [payload.vatAmount]       TVA (ignoré si franchise)
   * @param {string} [payload.pieceReference]  Ticket Z, bordereau, etc.
   * @param {string} [payload.note]            Note interne facultative
   * @param {?File}  attachment                PJ PDF/image (optionnel)
   * @returns {Promise<object>} L'entrée scellée créée (camelCase)
   */
  async createRevenueManualEntry(payload, attachment = null) {
    if (attachment instanceof File) {
      return this.upload("/revenue/manual-entries", attachment, payload, "attachment");
    }
    return this.post("/revenue/manual-entries", payload);
  },

  /**
   * EPIC-016 / 16.4 — Création d'une contre-écriture scellée à partir d'une
   * entrée source (montants miroir, `correctionOfEntryId` renseigné côté
   * backend).
   *
   * @param {string} sourceEntryId        UUID de l'entrée à corriger
   * @param {string} [description]        Override du libellé par défaut
   *                                      « Annulation écriture #N »
   * @returns {Promise<object>}
   */
  async createRevenueManualEntryCorrection(sourceEntryId, description = null) {
    const body = description ? { description } : {};
    return this.post(
      `/revenue/manual-entries/${encodeURIComponent(sourceEntryId)}/correction`,
      body,
    );
  },

  /**
   * EPIC-016 / 16.5 — Confirme un mois civil du livre des recettes (rituel
   * mensuel §7.H.E, BIC vente uniquement). Body vide. Réponse 201 avec la
   * confirmation créée. 409 `conflict` si déjà confirmé, 422 si organisation
   * non éligible ou year/month invalides.
   *
   * @param {number} year
   * @param {number} month
   * @returns {Promise<object>}
   */
  async confirmRevenueMonth(year, month) {
    return this.post(
      `/revenue/months/${encodeURIComponent(year)}/${encodeURIComponent(month)}/confirm`,
      {},
    );
  },

  /**
   * EPIC-016 / 16.5 — État d'un mois du livre des recettes (§7.H.E). Réponse
   * `{ confirmed, confirmedAt?, entriesCount, totalAmountInclVat,
   * manualEntriesCount?, invoicePaymentsCount?, hashDivergence? }`. Pré-remplit
   * le récapitulatif de la modale de confirmation.
   *
   * @param {number} year
   * @param {number} month
   * @returns {Promise<object>}
   */
  async getRevenueMonthStatus(year, month) {
    return this.get(
      `/revenue/months/${encodeURIComponent(year)}/${encodeURIComponent(month)}/status`,
    );
  },

  /**
   * EPIC-016 / 16.6 — Récap pré-clôture annuelle (§7.H.G). Alimente la
   * modale de confirmation avec entriesCount, breakdowns par type, totaux
   * ventilés par taux TVA, mois non confirmés BIC vente.
   *
   * @param {number} year
   * @returns {Promise<object>}
   */
  async getRevenueYearPreClosePreview(year) {
    return this.get(`/revenue/years/${encodeURIComponent(year)}/pre-close-preview`);
  },

  /**
   * EPIC-016 / 16.6 — Clôture définitive de l'exercice (§7.H.G). Réponse
   * 201 avec la `RevenueYearClosure` créée. 422 si l'exercice n'est pas
   * encore terminé, 409 si déjà clôturé.
   *
   * @param {number} year
   * @returns {Promise<object>}
   */
  async closeRevenueYear(year) {
    return this.post(`/revenue/years/${encodeURIComponent(year)}/close`, {});
  },

  /**
   * EPIC-016 / 16.3 — URL de l'export PDF provisoire (le navigateur déclenche
   * le download via `Content-Disposition: attachment`). Pas d'appel `fetch`
   * direct : on laisse le browser gérer le download avec le cookie de session.
   */
  buildRevenueRegisterProvisionalExportUrl(params = {}) {
    const query = new URLSearchParams();
    if (params.year !== undefined && params.year !== null && params.year !== "") {
      query.set("year", String(params.year));
    }
    // EPIC-099 / 99.27 — l'export provisoire est désormais annuel
    // uniquement (calque mécaniquement le PDF officiel de clôture). Le
    // paramètre `month` reste accepté côté backend mais le frontend ne
    // l'envoie plus.
    const qs = query.toString();
    // URL absolue (`API_CONFIG.baseURL`) comme les autres builders de
    // téléchargement (`invoicePdfUrl`, `das2.downloadPdfUrl`…) : le
    // `window.location.href` du controller doit viser l'origine de l'API,
    // pas l'origine de la page courante (sinon 404 quand le front est servi
    // sur un autre port/host que le backend).
    return `${API_CONFIG.baseURL}/revenue/register/export-provisional` + (qs ? "?" + qs : "");
  },

  /**
   * EPIC-016 / 16.6 — URL du PDF **officiel** d'un exercice clôturé (archivé
   * en rustfs lors de la clôture). À utiliser à la place de l'export
   * provisoire quand l'année est scellée : document opposable, sans bandeau
   * « non opposable ». URL absolue (cf. {@see buildRevenueRegisterProvisionalExportUrl}).
   */
  buildRevenueRegisterClosurePdfUrl({ year } = {}) {
    return `${API_CONFIG.baseURL}/revenue/years/${encodeURIComponent(year)}/pdf`;
  },

  /**
   * EPIC-018 / 18.3 — Registre des achats (§8.bis.E + §8.bis.F.1).
   * Gate `bic_vente` (décision D3) côté backend : `listEntries`,
   * `listClosures` et l'export retournent 403 `not_bic_vente_eligible`
   * si l'organisation n'a aucune activité BIC vente active.
   */
  purchaseRegister: {
    /**
     * @returns {Promise<{visible: boolean}>}
     */
    async getVisibility() {
      return api.get("/purchases/register/visibility");
    },

    /**
     * @param {{fiscalYear?: number, from?: string, to?: string, page?: number, perPage?: number}} filters
     * @returns {Promise<{data: object[], pagination: object, totals: object, period: object}>}
     */
    async listEntries(filters = {}) {
      const query = new URLSearchParams();
      if (filters.fiscalYear) query.set("fiscalYear", String(filters.fiscalYear));
      if (filters.from) query.set("from", filters.from);
      if (filters.to) query.set("to", filters.to);
      if (filters.page) query.set("page", String(filters.page));
      if (filters.perPage) query.set("perPage", String(filters.perPage));
      const qs = query.toString();
      return api.get("/purchases/register/entries" + (qs ? "?" + qs : ""));
    },

    /**
     * @returns {Promise<{data: object[]}>}
     */
    async listClosures() {
      return api.get("/purchases/register/closures");
    },

    /**
     * URL absolue du PDF de clôture archivé (téléchargement via
     * `window.location.href`). Absolue comme les autres builders de download :
     * doit viser l'origine de l'API, pas celle de la page.
     *
     * @param {string|number} fiscalYear
     * @returns {string}
     */
    closurePdfUrl(fiscalYear) {
      return `${API_CONFIG.baseURL}/purchases/register/closures/${encodeURIComponent(fiscalYear)}/pdf`;
    },

    /**
     * EPIC-018 / 18.4 — Page Tenue : file de traitement.
     *
     * @returns {Promise<{data: object[], count: number, entriesThisMonth: number}>}
     */
    async listPending() {
      return api.get("/purchases/register/processing/pending");
    },

    /**
     * EPIC-018 / 18.4 — polling lignes EN16931 d'une facture §8.
     *
     * @param {string} invoiceId
     * @returns {Promise<{lines: object[]}>}
     */
    async getInvoiceLines(invoiceId) {
      return api.get(
        "/purchases/register/processing/invoices/" + encodeURIComponent(invoiceId) + "/lines",
      );
    },

    /**
     * EPIC-018 / 18.4 — scellement bulk multi-factures.
     *
     * @param {{idempotencyKey: string, items: Array<object>}} body
     * @returns {Promise<{entriesCreated: number, processedInvoices: number}>}
     */
    async sealBulk(body) {
      return api.post("/purchases/register/processing/seal-bulk", body);
    },

    /**
     * EPIC-018 / 18.4 — marque une facture comme traitée sans écriture.
     *
     * @param {string} invoiceId
     * @returns {Promise<{processedAt: string}>}
     */
    async markNoMerchandise(invoiceId) {
      return api.post(
        "/purchases/register/processing/invoices/" + encodeURIComponent(invoiceId) + "/mark-no-merchandise",
        {},
      );
    },

    /**
     * EPIC-018 / 18.6 — récap modale clôture annuelle.
     *
     * @param {number} fiscalYear
     * @returns {Promise<{
     *   alreadyClosed: boolean,
     *   yearTerminated: boolean,
     *   fiscalYear: number,
     *   entriesCount: number,
     *   sealedFromInvoicesCount: number,
     *   manualEntriesCount: number,
     *   correctionsCount: number,
     *   sealedFromInvoicesAmount: string,
     *   manualEntriesAmount: string,
     *   correctionsAmount: string,
     *   totalsByVatRate: Array<{rate: string, amountExclVat: string, vatAmount: string, amountInclVat: string}>,
     *   totalAll: {amountExclVat: string, vatAmount: string, amountInclVat: string},
     * }>}
     */
    async getPreClosePreview(fiscalYear) {
      return api.get(
        "/purchases/register/years/" + encodeURIComponent(String(fiscalYear)) + "/pre-close-preview",
      );
    },

    /**
     * EPIC-018 / 18.6 — clôture annuelle utilisateur.
     *
     * @param {number} fiscalYear
     * @returns {Promise<{id: string, fiscalYear: number, closedAt: string, entriesCount: number, entriesSealedHash: string, pdfRustfsKey: string}>}
     */
    async closeYear(fiscalYear) {
      return api.post("/purchases/register/closures", { fiscalYear });
    },
  },

  // ---------------------------------------------------------------------------
  // EPIC-028 / 28.2 — Dashboard prospect §15.A (lecture seule).
  // ---------------------------------------------------------------------------
  dashboard: {
    /**
     * Récupère l'état complet du dashboard prospect (organisation sans SIRET
     * actif). Réponse agrégée : onboarding INPI, complétude profil aides,
     * statut éligibilité ACRE/ARE/ARCE, scénarios, activité récente.
     *
     * @returns {Promise<{
     *   onboardingStage: string,
     *   inpiFormality: object|null,
     *   profileCompleteness: { aidWizardCompleted: boolean, aidWizardSkipped: boolean },
     *   aidEligibilityStatus: { acre: string, are: string, arce: string },
     *   simulationScenarios: Array<object>,
     *   recentActivity: Array<object>,
     * }>}
     */
    async getProspect() {
      return api.get("/dashboard/prospect");
    },

    /**
     * Récupère l'agrégat dashboard d'une organisation active ou en attente de
     * SIRET (parcours A intermédiaire). Réponse pivot sur `mode` :
     *
     *   - `mode === "active"`           → toutes les clés financières + alerts
     *   - `mode === "awaiting_siret"`   → inpiFollowup + aids + activityFeed
     *
     * Certaines clés peuvent être `null` quand le module upstream n'est pas
     * encore livré (activityFeed, upcomingObligations).
     *
     * @returns {Promise<{ data: object }>}
     */
    async getMe() {
      return api.get("/dashboard/me");
    },
  },

  inpi: {
    /**
     * Upload d'une PJ rattachée à la formalité de création (étape 4 du
     * wizard demarrage-creation). L'attachmentId est conservé dans le
     * state local du wizard puis transmis au submit.
     *
     * @param {File}   file
     * @param {string} category  Une valeur de App\Enums\InpiAttachmentType
     *                           (`identity`, `address`, `non_condemnation`,
     *                           `rc_pro`, `decennale`, `cci_card`,
     *                           `financial_guarantee`, `other`).
     * @returns {Promise<{attachment: object}>}
     */
    async attachmentUpload(file, category) {
      return api.upload("/inpi/creation/attachments", file, { category });
    },

    /**
     * Finalise le wizard step 5 — body JSON cf. plan EPIC-008 / 8.4.
     *
     * @param {object} payload
     * @returns {Promise<{
     *   formality: object,
     *   subscription: { intentId: string, paymentLinkUrl: string,
     *                   amountHt: string, cadence: string, parcours: string },
     * }>}
     */
    async creationSubmit(payload) {
      return api.post("/inpi/creation/submit", payload);
    },

    /**
     * Arbre officiel des catégories d'activité du Guichet Unique (référentiel
     * vendoré N1→N4) pour le sélecteur guidé du wizard de création (99.98).
     *
     * @returns {Promise<{categories: Array<{
     *   codeN1: string, libelleN1: string, codeN2: string, libelleN2: string,
     *   codeN3: string, libelleN3: string, codeN4: string, libelleN4: string,
     *   codeFinal: string }>}>}
     */
    async activityCategories() {
      return api.get("/inpi/activity-categories");
    },

    /**
     * Liste les formalités INPI de l'organisation courante.
     *
     * @returns {Promise<{formalities: Array<object>}>}
     */
    async listFormalities() {
      return api.get("/inpi/formalities");
    },

    /**
     * Détail d'une formalité (formalité + PJ + events + régul ouvertes/résolues).
     *
     * @param {string} id  UUID de la formalité.
     * @returns {Promise<{
     *   formality: object,
     *   attachments: Array<object>,
     *   events: Array<object>,
     *   regularizationRequests: Array<object>,
     * }>}
     */
    async getFormality(id) {
      return api.get(`/inpi/formalities/${encodeURIComponent(id)}`);
    },

    /**
     * Push d'une formalité de modification LOCAL-* vers le GU INPI en
     * brouillon non signé + génération de l'URL profonde de signature
     * qualifiée. Le frontend redirige vers `redirectUrl` (procedures.inpi.fr).
     *
     * @param {string} formalityId
     * @returns {Promise<{redirectUrl: string, formality: object}>}
     */
    async modificationSign(formalityId) {
      return api.post(
        `/inpi/modification/${encodeURIComponent(formalityId)}/sign`,
        {},
      );
    },

    /**
     * Upload d'une PJ pour un requirement ouvert d'une régularisation
     * (page /inpi-regularisation.html).
     *
     * @param {string} formalityId
     * @param {File}   file
     * @param {string} requirementId  UUID FK inpi_regularization_request.
     * @returns {Promise<{attachment: object}>}
     */
    async regularizationUpload(formalityId, file, requirementId) {
      return api.upload(
        `/inpi/regularization/${encodeURIComponent(formalityId)}/upload`,
        file,
        { requirementId },
      );
    },

    /**
     * Renvoi de la formalité régularisée. Pour les créations : enregistre
     * la résolution (ntfy admin pour ré-dépôt manuel). Pour les modifs :
     * retourne l'URL de re-signature procedures.inpi.fr.
     *
     * @param {string} formalityId
     * @returns {Promise<{formality: object, redirectUrl?: string}>}
     */
    async regularizationSubmit(formalityId) {
      return api.post(
        `/inpi/regularization/${encodeURIComponent(formalityId)}/submit`,
        {},
      );
    },
  },

  // ---------------------------------------------------------------------------
  // URSSAF — déclaration CA, estimation (§10, EPIC-020)
  //
  // - `submit` exige un header `Idempotency-Key` (UUID v4) généré côté
  //   client. Le backend rejette en 400 si absent.
  // - `estimate` n'a pas d'`id` de déclaration dans l'URL : le backend
  //   identifie la période via `periodLabel`.
  //
  // EPIC-099 / 99.37 — le télépaiement (API060) et les mandats SEPA URSSAF
  // (API070/071/075) sont hors périmètre Notamicro : aucun endpoint exposé.
  // ---------------------------------------------------------------------------
  urssaf: {
    async getCurrent() {
      return api.get("/urssaf/declaration/current");
    },

    async patchAdjustment(declarationId, payload) {
      return api.patch(
        `/urssaf/declaration/${encodeURIComponent(declarationId)}/manual-adjustment`,
        payload,
      );
    },

    async estimate(payload) {
      return api.post("/urssaf/estimation", payload);
    },

    // EPIC-099.88 — mode assisté V1 : le ME reporte les montants sur
    // autoentrepreneur.urssaf.fr puis saisit l'accusé reçu. Pas de header
    // Idempotency-Key (saisie unique, re-soumission bloquée par l'état).
    async submit(declarationId, { receiptNumber, receiptDate }) {
      return api.post(
        `/urssaf/declaration/${encodeURIComponent(declarationId)}/submit`,
        { receiptNumber, receiptDate },
      );
    },

    async listDeclarations(page = 1) {
      return api.get("/urssaf/declarations", { page });
    },
  },

  // ---------------------------------------------------------------------------
  // EPIC-025 / 25.5 — Aide à la déclaration de revenus (§14, 2042-C-PRO).
  //
  // - `getYearlySummary(year)` retourne le récap fiscal d'un exercice :
  //   CA brut par catégorie, abattements, revenu net, cases 2042-C-PRO,
  //   `declaredForYear` (bool) et `organizationCreatedYear` (borne nav).
  // - `downloadPdf(year)` n'effectue aucun fetch côté JS : le controller
  //   appelle `window.open(this.pdfUrl(year), '_blank')` directement.
  //   Le serveur émet `Content-Disposition: attachment`.
  // - `markDone(year, idempotencyKey)` exige un `Idempotency-Key` UUID v4
  //   (rejet 400 sinon). 409 si déjà marqué.
  // ---------------------------------------------------------------------------
  incomeTax: {
    async getYearlySummary(year) {
      return api.get("/income-tax/yearly-summary", { year });
    },

    pdfUrl(year) {
      return `${API_CONFIG.baseURL}/income-tax/${encodeURIComponent(year)}/pdf`;
    },

    async markDone(year, idempotencyKey) {
      return api.post(
        `/income-tax/${encodeURIComponent(year)}/mark-done`,
        {},
        { "Idempotency-Key": idempotencyKey },
      );
    },
  },

  // ---------------------------------------------------------------------------
  // EPIC-023 / 23.5 — DAS2 §12 (détection auto + validation manuelle).
  //
  // - `getDeclaration(year)` retourne l'état courant : entête déclaration,
  //   totaux agrégés, résumé des bénéficiaires (`status`, `computed_total_ttc`,
  //   `overrides`).
  // - `refresh(year)` relance la détection déterministe côté serveur
  //   (`POST .../refresh`) — upsert idempotent des `das2_beneficiary` et
  //   recalcul du flag `das2_candidate` sur les `purchase_invoice_metadata`.
  // - `updateBeneficiary(id, action, overrides?)` regroupe `validate`,
  //   `exclude`, `reinclude`, `override` côté PATCH (cf. 23.4 §body).
  // - `submit(year, payload, idempotencyKey)` : saisie accusé DGFiP + bascule
  //   `submission_status='submitted'` + archivage vault `auto_das2_filing`.
  //   `Idempotency-Key` UUID v4 obligatoire (rejet 400 sinon).
  // - `downloadTxtUrl` / `downloadPdfUrl` retournent l'URL absolue à
  //   utiliser pour une redirection navigateur directe (le serveur émet
  //   `Content-Disposition: attachment`). Pas de fetch+blob côté JS.
  // ---------------------------------------------------------------------------
  das2: {
    async listDeclarations(page = 1, perPage = 10) {
      return api.get("/das2/declarations", { page, perPage });
    },

    async getDeclaration(year) {
      return api.get(`/das2/declarations/${encodeURIComponent(year)}`);
    },

    async refresh(year) {
      return api.post(`/das2/declarations/${encodeURIComponent(year)}/refresh`, {});
    },

    async listBeneficiaries(year) {
      return api.get(`/das2/declarations/${encodeURIComponent(year)}/beneficiaries`);
    },

    async updateBeneficiary(beneficiaryId, action, overrides = null) {
      const payload = { action };
      if (overrides !== null) {
        payload.overrides = overrides;
      }
      return api.patch(
        `/das2/beneficiaries/${encodeURIComponent(beneficiaryId)}`,
        payload,
      );
    },

    async submit(year, payload, idempotencyKey) {
      return api.post(
        `/das2/declarations/${encodeURIComponent(year)}/submit`,
        payload,
        { "Idempotency-Key": idempotencyKey },
      );
    },

    downloadTxtUrl(year) {
      return `${API_CONFIG.baseURL}/das2/declarations/${encodeURIComponent(year)}/download/txt`;
    },

    downloadPdfUrl(year) {
      return `${API_CONFIG.baseURL}/das2/declarations/${encodeURIComponent(year)}/download/pdf`;
    },
  },

  // ---------------------------------------------------------------------------
  // EPIC-020 / 20.6 — France Travail §10.B (déclaration mensuelle).
  // ---------------------------------------------------------------------------
  franceTravail: {
    async getCurrentMonth(monthYear) {
      return api.get("/france-travail/declarations/current-month", { monthYear });
    },

    async listDeclarations(page = 1, perPage = 10) {
      return api.get("/france-travail/declarations", { page, perPage });
    },

    async create(payload) {
      return api.post("/france-travail/declarations", payload);
    },

    async update(declarationId, payload) {
      return api.patch(
        `/france-travail/declarations/${encodeURIComponent(declarationId)}`,
        payload,
      );
    },
  },

  // ---------------------------------------------------------------------------
  // EPIC-029 / 29.4 — Flux d'activité §15.C (lecture seule).
  //
  // - `list(params)` interroge `GET /activity-feed` avec pagination,
  //   filtres URL (`domain` CSV, `startDate`, `endDate`, `clientId`,
  //   `invoiceId`, `quoteId`) et tri `sort` (défaut `-occurredAt`).
  //   Réponse enveloppée `{ data, pagination }`. Le bloc dashboard
  //   (29.4) ne passe que `perPage=10` + `sort=-occurredAt` ; la page
  //   dédiée (29.5) consommera l'ensemble des filtres.
  // ---------------------------------------------------------------------------
  activityFeed: {
    async list(params = {}) {
      return api.get("/activity-feed", params);
    },
  },

  // ---------------------------------------------------------------------------
  // EPIC-030 / 30.4 — Calendrier d'obligations §15.D (vue 12 mois glissants).
  //
  // - `list({ perPage })` interroge `GET /upcoming-obligations`.
  //   Réponse enveloppée `{ data, pagination }` (contrat 30.3). Chaque
  //   item porte `{ domain, label, dueDate, status, isMarkAsDone,
  //   actionUrl }`. Le bloc dashboard (30.4) ne passe que `perPage=10` ;
  //   la page dédiée (30.5) consommera filtres + pagination.
  //
  //   Note (99.62) : il n'existe pas de POST « mark-done » générique sur
  //   `actionUrl` (page frontend, pas un endpoint). La réalisation des
  //   obligations système s'enregistre sur la page spécialisée (lien
  //   « Préparer → »). Seuls les rappels personnels exposent un mark-done
  //   réel, via `POST /me/personal-reminders/{id}/mark-done`.
  // ---------------------------------------------------------------------------
  upcomingObligations: {
    async list(params = {}) {
      return api.get("/upcoming-obligations", params);
    },
  },

  // ---------------------------------------------------------------------------
  // EPIC-035 / 35.4 — Coffre-fort §24 (listing paginé + actions liste).
  //
  // - `vault.list(params)` interroge `GET /v1/vault` avec filtres en
  //   querystring (`type`, `category`, `fiscalYear`, `dateFrom`, `dateTo`,
  //   `search`, `includeArchived`, `page`, `perPage`, `sort`). Réponse :
  //   `{ data, pagination, meta: { quota: { usedBytes, quotaBytes,
  //   usedPercent }, autoCount } }`.
  // - `vault.archive(id)` POST `/v1/vault/{id}/archive` : soft-delete
  //   un document `manual_upload` (409 `vault_document_immutable` sur les
  //   sources `auto_*`).
  // - `vault.get(id)` GET `/v1/vault/{id}` : retourne `{ data, links:
  //   { sourcePrimary: { kind, url } | null } }`. Utilisé par la page détail
  //   `/vault/{id}`.
  // - `vault.update(id, payload)` PATCH `/v1/vault/{id}` : modifie les
  //   métadonnées éditables d'un document `manual_upload` actif (title,
  //   category, documentDate, fiscalYear, notes).
  // - `vault.create({ file, title, category, documentDate, fiscalYear,
  //   notes? })` POST multipart `/v1/vault` : upload manuel (35.6).
  // - `downloadUrl(id)` retourne l'URL absolue de redirection 302 vers
  //   l'URL signée rustfs (15 min de validité). Navigation directe côté
  //   controller, pas de fetch + blob.
  // ---------------------------------------------------------------------------
  vault: {
    async list(params = {}) {
      return api.get("/v1/vault", params);
    },

    async get(id) {
      return api.get(`/v1/vault/${encodeURIComponent(id)}`);
    },

    async create({ file, ...metadata }) {
      return api.upload("/v1/vault", file, metadata);
    },

    async update(id, payload) {
      return api.patch(`/v1/vault/${encodeURIComponent(id)}`, payload);
    },

    async archive(id) {
      return api.post(`/v1/vault/${encodeURIComponent(id)}/archive`, {});
    },

    downloadUrl(id) {
      return `${API_CONFIG.baseURL}/v1/vault/${encodeURIComponent(id)}/download`;
    },
  },

  // ---------------------------------------------------------------------------
  // EPIC-042 / 42.4 — Transactions de paiement §7.I (modèle unifié).
  //
  // - `payments.list(params)` interroge `GET /v1/payments` (paginé).
  //   Filtres plats acceptés : `source`, `status`, `providerId`, `invoiceId`,
  //   `from` (YYYY-MM-DD), `to`, `page`, `perPage` (cap 100, défaut 25).
  //   Réponse : `{ data: PaymentTransaction[], pagination: { page, perPage,
  //   total, totalPages } }`.
  // - `payments.pending()` interroge `GET /v1/payments/pending` : retourne
  //   directement un tableau JSON (pas d'enveloppe) de transactions détachées
  //   au-dessus du seuil légal B2C ou avec brouillon, enrichies de
  //   `providerLabel`, `hasDraft`, `aboveB2CThreshold` (§7.I.F).
  // - `payments.update(id, payload)` PATCH `/v1/payments/{id}` : patch
  //   partiel sur `transactionDate`, `amount`, `paymentSource`, `clientName`,
  //   `notes`. Voie « Identifier sans facturer » du flux pending : envoyer
  //   `{ clientName: '...' }` sort la ligne de pending.
  // - `payments.dismiss(id)` POST `/v1/payments/{id}/dismiss` : voie
  //   « Ignorer » (soft-dismiss persistant via colonne `dismissed_at`, 42.3).
  //   Réponse 204 — idempotent côté serveur.
  // ---------------------------------------------------------------------------
  payments: {
    async list(params = {}) {
      return api.get("/v1/payments", params);
    },

    async pending() {
      return api.get("/v1/payments/pending");
    },

    async update(id, payload) {
      return api.patch(`/v1/payments/${encodeURIComponent(id)}`, payload);
    },

    async dismiss(id) {
      return api.post(`/v1/payments/${encodeURIComponent(id)}/dismiss`, {});
    },

    // ---------------------------------------------------------------------------
    // EPIC-042 / 42.5 — helpers consommés par `payments-edition` et par la
    // section « Suivi des paiements » de la fiche facture (refactor sur les
    // nouveaux endpoints /v1/payments*, en remplacement des anciens
    // /v1/invoices/{id}/payments).
    // ---------------------------------------------------------------------------

    async get(id) {
      return api.get(`/v1/payments/${encodeURIComponent(id)}`);
    },

    /**
     * GET /v1/payments/{id}/history — timeline d'audit
     * `payment_transaction_event` (EPIC-099 / 99.12 — §7.I.B). Renvoie
     * `{ data: [{ id, eventType, actorKind, actorUserId, before, after,
     * changedFields, metadata, happenedAt }] }` chronologique ASC.
     */
    async history(id) {
      return api.get(`/v1/payments/${encodeURIComponent(id)}/history`);
    },

    /**
     * POST /v1/payments — création manuelle. `Idempotency-Key` UUID v4
     * obligatoire côté backend pour absorber un double-clic ou un rejeu réseau.
     *
     * @param {object} payload  body JSON (`transactionDate`, `amount`,
     *   `paymentSource`, `paymentProviderId`, `externalId`, `invoiceId`,
     *   `notes`)
     * @param {string} idempotencyKey  UUID v4 généré côté client
     */
    async create(payload, idempotencyKey) {
      return api.post("/v1/payments", payload, {
        "Idempotency-Key": idempotencyKey,
      });
    },

    /**
     * POST /v1/payments/{id}/archive — soft-delete (`status='archived'`).
     * Cohérent CLAUDE.md *« Aucun DELETE »*.
     */
    async archive(id) {
      return api.post(`/v1/payments/${encodeURIComponent(id)}/archive`, {});
    },

    /**
     * POST /v1/payments/{id}/counter-entry — contre-écriture
     * (transaction nouvelle, `amount < 0`, `transaction_date = today`),
     * utilisée pour annuler une session caisse close ou un Wero confirmé.
     */
    async counterEntry(id) {
      return api.post(
        `/v1/payments/${encodeURIComponent(id)}/counter-entry`,
        {},
      );
    },

    /**
     * POST /v1/payments/{id}/attach — rattachement à une facture
     * (renseigne `invoice_id` après coup). Recalcule le statut paiement
     * côté backend.
     */
    async attach(id, invoiceId) {
      return api.post(`/v1/payments/${encodeURIComponent(id)}/attach`, {
        invoiceId,
      });
    },
  },

  // ---------------------------------------------------------------------------
  // EPIC-042 / 42.5 + 42.6 — Moyens de paiement « autres » paramétrés par le
  // ME (table `payment_provider`, §7.I.H). Consommé par :
  //   - 42.5 : `list()` peuple le select provider de `/paiements-edition.html`
  //     quand `paymentSource=other` (filtrage actifs/non-archivés appliqué
  //     côté UI).
  //   - 42.6 : CRUD complet exposé à la page `/parametres-encaissement.html`
  //     (liste + édition réutilisable + désactivation/réactivation). La 422
  //     `payment_provider_has_recent_transactions` retournée par `deactivate()`
  //     porte `details.provider` à jour — le frontend la traite comme une
  //     « erreur informative » plutôt qu'un échec total.
  // ---------------------------------------------------------------------------
  // ---------------------------------------------------------------------------
  // EPIC-043 / 43.4 — Caisse espèces (§7.J)
  // ---------------------------------------------------------------------------
  //   - Sessions ouvertes / clôturées avec fond de caisse, mouvements manuels
  //     et transactions individuelles. Statuts `open` | `closed` uniquement
  //     (pas de cancelled — soft-delete projet).
  //   - L'ouverture POST nécessite un `Idempotency-Key` (UUID v4) côté
  //     backend pour neutraliser les doubles soumissions (double-clic,
  //     reload pendant la requête).
  //   - Le PATCH du sous-EPIC 43.4 ne touche que `openingBalance` ; les
  //     autres champs (label, activityId, sessionDate) ne sont pas
  //     modifiables après ouverture.
  // ---------------------------------------------------------------------------
  cashRegister: {
    async listSessions({ page = 1, perPage = 25, year = null, month = null, status = null } = {}) {
      const params = { page, perPage };
      if (year) params.year = year;
      if (month) params.month = month;
      if (status) params.status = status;
      return api.get("/v1/cash-register/sessions", params);
    },

    async getSession(id) {
      return api.get(`/v1/cash-register/sessions/${encodeURIComponent(id)}`);
    },

    async createSession(payload, idempotencyKey) {
      return api.post("/v1/cash-register/sessions", payload, {
        "Idempotency-Key": idempotencyKey,
      });
    },

    async updateSession(id, payload) {
      return api.patch(`/v1/cash-register/sessions/${encodeURIComponent(id)}`, payload);
    },

    async addMovement(id, payload) {
      return api.post(
        `/v1/cash-register/sessions/${encodeURIComponent(id)}/movements`,
        payload,
      );
    },

    async addPendingTransaction(id, payload) {
      return api.post(
        `/v1/cash-register/sessions/${encodeURIComponent(id)}/transactions`,
        payload,
      );
    },

    async closeSession(id, payload, idempotencyKey) {
      return api.post(
        `/v1/cash-register/sessions/${encodeURIComponent(id)}/close`,
        payload,
        { "Idempotency-Key": idempotencyKey },
      );
    },

    // EPIC-099.20 — agrégat léger pour la pastille sidebar §7.J.A et la
    // tonalité encart dashboard §7.J.H. Exposé sous /me/* (MeController)
    // plutôt que /v1/cash-register/* car la sémantique est « état caisse
    // du tenant courant », pas une opération sur une ressource caisse.
    // Réponse : { openSessionsCount: int, oldestOpenedAt: iso8601|null }.
    async status() {
      return api.get("/me/cash-register-status");
    },
  },

  paymentProviders: {
    /** Catalogue partagé (EPIC-099 / 99.13 — §7.L.D). */
    async catalog() {
      return api.get("/v1/payment-providers/catalog");
    },

    /** Instances per-ME (`organization_payment_provider`). */
    async list() {
      return api.get("/v1/payment-providers");
    },

    async get(id) {
      return api.get(`/v1/payment-providers/${encodeURIComponent(id)}`);
    },

    async create(payload) {
      return api.post("/v1/payment-providers", payload);
    },

    async update(id, payload) {
      return api.patch(`/v1/payment-providers/${encodeURIComponent(id)}`, payload);
    },

    async archive(id) {
      return api.delete(`/v1/payment-providers/${encodeURIComponent(id)}`);
    },

    async deactivate(id) {
      return api.post(`/v1/payment-providers/${encodeURIComponent(id)}/deactivate`, {});
    },

    async reactivate(id) {
      return api.post(`/v1/payment-providers/${encodeURIComponent(id)}/reactivate`, {});
    },
  },

  // EPIC-045 / 45.5 — Connector SumUp §7.K
  sumup: {
    /**
     * Initie le flow OAuth. Retourne `{ url, state }` ; le caller redirige
     * `window.location` sur `url`. Le flag `ack=1` doit avoir été coché
     * par l'utilisateur (texte d'acquittement spec §7.K.A) avant l'appel.
     */
    async oauthInit(ack = false) {
      const qs = ack ? "?ack=1" : "";
      return api.get(`/v1/payment-providers/sumup/oauth-init${qs}`);
    },

    async disconnect(id) {
      return api.post(`/v1/payment-providers/sumup/${encodeURIComponent(id)}/disconnect`, {});
    },

    /**
     * URL absolue (relative au backend) du PNG QR téléchargeable. Le
     * caller pose ce string sur `<a href>` + `download` ; le navigateur
     * récupère le PNG en `Content-Disposition: attachment`.
     */
    qrUrl(id) {
      return `${API_BASE_URL}/v1/payment-providers/sumup/${encodeURIComponent(id)}/qr.png`;
    },
  },

  // EPIC-045 / 45.4 — Demandes de facture par QR §7.K.E
  invoiceRequests: {
    async list({ status, page = 1, perPage = 25 } = {}) {
      const params = new URLSearchParams();
      if (status) params.set("status", status);
      params.set("page", String(page));
      params.set("perPage", String(perPage));
      return api.get(`/v1/invoice-requests?${params.toString()}`);
    },

    async get(id) {
      return api.get(`/v1/invoice-requests/${encodeURIComponent(id)}`);
    },

    async link(id, paymentTransactionId) {
      return api.post(`/v1/invoice-requests/${encodeURIComponent(id)}/link`, {
        paymentTransactionId,
      });
    },

    async reject(id, reason) {
      return api.post(`/v1/invoice-requests/${encodeURIComponent(id)}/reject`, {
        reason,
      });
    },
  },
};

export { APIError };
export default api;
